import {
  CreateEventBookingEventPayload,
  CreateSpacePayload,
  CreateVenuePayload,
  GetEventBookingEventParams,
  GetEventBookingEventsParams,
  GetRestaurantsQuery,
  GetSpaceParams,
  GetSpacesParams,
  GetTicketedEventParams,
  GetVenueParams,
  GetVenuesParams,
  PaginatedResponse,
  UpdateSpacePayload,
  UpdateTicketedEventPayload,
  UpdateVenuePayload,
} from '@Types/api';
import { EventBookingEvent } from '@Types/eventBooking';
import {
  Fish,
  FishV2,
  PaymentSources,
  SharedSubscriptionAccessUpdate,
  SharedSubscriptionFish,
  SubscriptionTier,
} from '@Types/fish';
import type { Flow } from '@Types/flow';
import type { AttachedInboxTypeOption } from '@Types/triggers';
import type { CustomFormInput } from '@Types/customForm';
import { FlowStage } from '@Types/flowStage';
import { Purchase } from '@Types/purchase';
import { Referral } from '@Types/referral';
import type {
  CreateRegistrationCodePayload,
  PreviewRegistrationCodePayload,
  RegistrationCode,
  RegistrationCodePreview,
  RegistrationCodeSubscription,
  RegistrationCodeUsage,
  UpdateRegistrationCodePayload,
} from '@Types/registrationCode';
import {
  statsRangeParams,
  type EngagementStats,
  type FirstTouchStats,
  type StatsRange,
} from '@Types/messagingStats';
import { User } from '@Types/user';
import type { Tag } from '@Types/tags';
import type { UserRole } from '@Types/userRole';
import type {
  Soul,
  CreateSoulPayload,
  UpdateSoulPayload,
  AiChat,
  AiChatDetail,
  AiSuggestion,
} from '@Types/soul';
import type { AgentProfile, AgentOperation, AgentContact } from '@Types/agent';
import type { ShiftReportSummary, ShiftReportDetail } from '@Types/shiftReport';
import type {
  EventReportSummary,
  EventReportDetail,
  EventStaffMember,
} from '@Types/eventReport';
import type {
  WeddingReportSummary,
  WeddingReportDetail,
} from '@Types/weddingReport';
import type {
  TourReportSummary,
  TourReportDetail,
} from '@Types/tourReport';
import type {
  HotelReportSummary,
  HotelReportDetail,
} from '@Types/hotelReport';
import type {
  EvalSuiteSummary,
  EvalSuiteDetail,
  EvalCase,
  EvalRunSummary,
  EvalRunDetail,
  CreateSuitePayload,
  UpdateSuitePayload,
  CreateCasePayload,
  UpdateCasePayload,
  RunSuitePayload,
} from '@Types/eval';
import type {
  SoulExample,
  CreateSoulExamplePayload,
  UpdateSoulExamplePayload,
} from '@Types/soulExample';
import {
  ConfirmPaymentIntentRequest,
  ConfirmPaymentIntentResponse,
  DiningOption,
  Menu,
  MenusResponse,
  Order,
  OrderResponse,
  PaymentIntentCreateResponse,
  ServiceCharge,
  UpdatePaymentIntentRequest,
  UpdatePaymentIntentResponse,
} from '@Types/toast';
import { Space, Venue, VenueUserAssignment } from '@Types/venue';
import type {
  ToastGuid,
  CreateToastGuidPayload,
  UpdateToastGuidPayload,
} from '@Types/toastGuid';
import type {
  ToastGuidGroup,
  CreateToastGuidGroupPayload,
  UpdateToastGuidGroupPayload,
} from '@Types/toastGuidGroup';
import type {
  ToastGuidSetting,
  UpdateToastGuidSettingPayload,
} from '@Types/toastGuidSetting';
import type {
  PromoCode,
  CreatePromoCodePayload,
  UpdatePromoCodePayload,
} from '@Types/promoCode';
import type {
  PartnerReferralCode,
  PartnerReferralCodeSubscription,
  CreatePartnerReferralCodePayload,
  UpdatePartnerReferralCodePayload,
} from '@Types/partnerReferralCode';
import type { Redemption } from '@Types/redemption';
import type {
  QuickMessage,
  QuickMessageMethod,
} from '@Types/messageTemplate';
import type {
  ContentSlot,
  ContentSlotSummary,
  CreateContentSlotPayload,
  SaveContentSlotDraftPayload,
  TenantApp,
  UpdateContentSlotPayload,
} from '@Types/contentSlot';
import { buildJsonApiQuery } from '@Utils/apiUtil';
import { buildUrl } from '@Utils/searchParams';
import cookies from 'browser-cookies';
import type { CreateUserRolePermission } from '@Types/userRole';

import { requireTenantId } from './stores/tenantStore';

const API_ROOT = window.location.host.includes('localhost')
  ? 'http://localhost:3030'
  : import.meta.env.VITE_API_URL;
const DOMAIN = `${API_ROOT}/v1`;
const DOMAIN_V2 = `${API_ROOT}/v2`;

const QUICK_MESSAGE_RESULT_LIMIT = 50;

const serviceMethods = {
  createFlow: async (
    flow: Omit<Flow, 'id' | 'createdAt' | 'updatedAt' | 'deletedAt'>,
  ) => {
    const res = await _protectedFetch(uri_flow, {
      method: 'POST',
      body: JSON.stringify(flow),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  updateFlow: async (payload: Partial<Flow>) => {
    const res = await _protectedFetch(uri_flow + `?id=${payload.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        payload,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  deleteFlow: async (id: string, userId: string) => {
    const res = await _protectedFetch(uri_flow, {
      method: 'DELETE',
      body: JSON.stringify({
        id,
        userId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },

  createFlowCategory: async (categoryData: Record<string, unknown>) => {
    const res = await _protectedFetch(uri_flow_category, {
      method: 'POST',
      body: JSON.stringify(categoryData),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return res;
  },
  updateFlowCategory: async (
    categoryId: string,
    payload: Record<string, unknown>,
  ) => {
    const res = await _protectedFetch(`${uri_flow_category}/${categoryId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      console.error('Error updating Flow Category: HTTP', res.status);
      throw new Error(`Error updating Flow Category (${res.status})`);
    }

    return await res.json();
  },
  getFlowCategories: async (searchParams = {}) => {
    try {
      const queryParams = new URLSearchParams(searchParams).toString();
      const url = queryParams
        ? `${uri_flow_category}?${queryParams}`
        : `${uri_flow_category}`;

      const res = await _protectedFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return await res.json();
    } catch (error) {
      console.error('Error getting Flow Categories:', error);
    }
  },
  getFlowCategory: async (categoryId: string, searchParams = {}) => {
    try {
      const queryParams = new URLSearchParams(searchParams).toString();
      const url = queryParams
        ? `${uri_flow_category}/${categoryId}?${queryParams}`
        : `${uri_flow_category}/${categoryId}`;

      const res = await _protectedFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return await res.json();
    } catch (error) {
      console.error('Error getting Flow Categories:', error);
    }
  },
  deleteFlowCategory: async (categoryId: string, deletedBy: string) => {
    const res = await _protectedFetch(`${uri_flow_category}/${categoryId}`, {
      method: 'DELETE',
      body: JSON.stringify({ deletedBy }),
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`Failed to delete FlowCategory (${res.status})`);
    }

    return res;
  },

  createFlowStage: async (
    flowStageObj: Omit<
      FlowStage,
      'id' | 'createdAt' | 'updatedAt' | 'deletedAt'
    >,
  ) => {
    const res = await _protectedFetch(uri_flow_stage, {
      method: 'POST',
      body: JSON.stringify(flowStageObj),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  updateFlowStage: async (flowStageObj: any) => {
    const res = await _protectedFetch(
      uri_flow_stage + `?flowStageId=${flowStageObj.id}`,
      {
        method: 'PUT',
        body: JSON.stringify({ title: flowStageObj.title }),
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    return res;
  },
  deleteFlowStage: async (id: string) => {
    const res = await _protectedFetch(uri_flow_stage, {
      method: 'DELETE',
      body: JSON.stringify({
        id,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  getFlowStageById: async () => {
    const res = await _protectedFetch(uri_flow_stage, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  getAllFlowStages: async () => {
    const res = await _protectedFetch(uri_get_all_flow_stages, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return await res.json();
  },
  getAllFlows: async () => {
    const res = await _protectedFetch(uri_get_all_flows, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return await res.json();
  },
  getFlowById: async (id: string) => {
    const res = await _protectedFetch(`${uri_flow}?id=${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  getFullFlowById: async (id: string) => {
    const res = await _protectedFetch(`${uri_get_full_flow}?id=${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  getFlowInstance: async (flowInstanceId: string) => {
    const url = `${uri_flow}/instance/${flowInstanceId}`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`Failed to load flow instance (${res.status})`);
    }

    return await res.json();
  },
  getFlowInstances: async (
    searchParams = {},
    flowId,
    activeStageId,
    categoryId,
    fishId = undefined,
  ) => {
    const queryParams = new URLSearchParams(searchParams);

    if (flowId) {
      queryParams.set('flowId', flowId);
    }
    if (activeStageId) {
      queryParams.set('activeStageId', activeStageId);
    }
    if (categoryId) {
      queryParams.set('categoryId', categoryId);
    }
    if (fishId) {
      queryParams.set('fishId', fishId);
    }

    const url = queryParams
      ? `${uri_flow}/instances?${queryParams.toString()}`
      : `${uri_flow}/instances`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return await res.json();
  },
  emitDebugLapse: async (payload: {
    fishId: string;
    flowStageId: string;
    flowInstanceId?: string;
    evaluate?: boolean;
    lastStageExecutedAt?: string;
  }) => {
    const res = await _protectedFetch(`${uri_flow}/_debug/emit-lapse`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: payload }),
    });
    if (!res.ok) {
      throw new Error(`Error emitting lapse (${res.status})`);
    }
    return res.json();
  },
  emitDebugUpcoming: async (payload: {
    flowStageId: string;
    // An activity id (activity stages) or a subscription id (renewal stages).
    entityId: string;
    evaluate?: boolean;
  }) => {
    const res = await _protectedFetch(`${uri_flow}/_debug/emit-upcoming`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: payload }),
    });
    if (!res.ok) {
      throw new Error(`Error emitting upcoming stage (${res.status})`);
    }
    return res.json();
  },
  emitDebugInboundSms: async (payload: {
    fishId: string;
    inboxId: string;
    body: string;
  }) => {
    const res = await _protectedFetch(`${uri_flow}/_debug/emit-inbound-sms`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: payload }),
    });
    if (!res.ok) {
      throw new Error(`Error emitting inbound SMS (${res.status})`);
    }
    return res.json();
  },
  getFlowDebugLogs: async (params: {
    flowId?: string;
    fishId?: string;
    since?: string;
    limit?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params.flowId) queryParams.set('flowId', params.flowId);
    if (params.fishId) queryParams.set('fishId', params.fishId);
    if (params.since) queryParams.set('since', params.since);
    if (params.limit) queryParams.set('limit', String(params.limit));

    const res = await _protectedFetch(
      `${uri_flow}/_debug/logs?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      },
    );
    if (!res.ok) {
      throw new Error(`Error loading flow debug logs (${res.status})`);
    }
    return res.json();
  },
  getAllForms: async () => {
    const res = await _protectedFetch(uri_get_all_forms, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  getAllCustomForms: async () => {
    const res = await _protectedFetch(uri_get_all_custom_forms, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  getCustomFormById: async (id: string) => {
    const res = await _protectedFetch(`${uri_custom_form}?id=${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  createCustomForm: async (customFormObject: any) => {
    const res = await _protectedFetch(uri_custom_form, {
      method: 'POST',
      body: JSON.stringify(customFormObject),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  updateCustomForm: async (id: string, body: CustomFormInput) => {
    const res = await _protectedFetch(`${uri_custom_form}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.message || `Error saving custom form (${res.status})`);
    }
    return res.json();
  },
  deleteCustomForm: async (id: string): Promise<void> => {
    const res = await _protectedFetch(`${uri_custom_form}/${id}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.message || `Error deleting custom form (${res.status})`);
    }
  },
  getCustomFormResponse: async (id: string) => {
    const res = await _protectedFetch(`${uri_custom_form_response}?id=${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  createCustomFormResponse: async (customFormObject: any) => {
    const res = await _protectedFetch(uri_custom_form_response, {
      method: 'POST',
      body: JSON.stringify(customFormObject),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  getFlowStageTypes: async () => {
    const res = await _protectedFetch(uri_get_flow_stage_types, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  getFlowStageResultsByFishId: async (id: string) => {
    const res = await _protectedFetch(`${uri_flow_stage_results}?id=${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  getTypeformResponse: async (id: string) => {
    const res = await _protectedFetch(`${uri_typeform_response}/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  createOutboundMessage: async (outboundMessageObject: any) => {
    const res = await _protectedFetch(uri_outbound_message, {
      method: 'POST',
      body: JSON.stringify(outboundMessageObject),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  readOutboundMessage: async (id: string) => {
    const res = await _protectedFetch(`${uri_outbound_message}/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  readOutboundMessages: async (filter: string = 'unsent', fishId: string) => {
    const queryParams = new URLSearchParams({
      filter,
      ...(fishId && { fishId }),
    }).toString();

    const res = await _protectedFetch(
      `${uri_outbound_message}?${queryParams}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    return res;
  },
  getOutboundMessages: async (searchParams = {}) => {
    try {
      const queryParams = new URLSearchParams(searchParams).toString();

      const url = queryParams
        ? `${uri_outbound_message}?${queryParams}`
        : `${uri_outbound_message}`;

      const res = await _protectedFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return await res.json();
    } catch (error) {
      console.error('Error getting message templates:', error);
    }
  },
  updateOutboundMessage: async (id: string, updateObject: any) => {
    const res = await _protectedFetch(`${uri_outbound_message}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updateObject),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return res;
  },
  getOutboundMessageRecipients: async (id: string) => {
    const res = await _protectedFetch(
      `${uri_outbound_message}/${id}/recipients`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    return res.json();
  },
  deleteOutboundMessage: async (id: string) => {
    const res = await _protectedFetch(`${uri_outbound_message}/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return res;
  },
  deleteOutboundMessages: async (ids: string[]) => {
    const res = await _protectedFetch(uri_outbound_message, {
      method: 'DELETE',
      body: JSON.stringify({ ids: ids }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return res;
  },
  immediateSendSMS: async (payload: any) => {
    // payload should include: to, body, fishId, and senderId
    const res = await _protectedFetch(uri_immediate_send_sms, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  getAllTagsWithQuery: async (query?: Record<string, any>): Promise<Tag[]> => {
    const url = buildUrl(uri_get_all_tags_with_query, {
      'page[size]': '-1',
      ...query,
    });
    const res = await _protectedFetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    try {
      const data = await res.json();
      return data;
    } catch (error) {
      console.error(error);
      return [];
    }
  },
  readAllTags: async (pageNumber?: number, pageSize?: number) => {
    let url: string;
    if (pageNumber && pageSize) {
      url = `${uri_tag}?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    } else {
      url = uri_tag;
    }
    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const tags = await res.json();
    return tags;
  },
  readTagById: async (id: string) => {
    const res = await _protectedFetch(`${uri_tag}/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  updateTag: async (id: string, updateObject: any) => {
    const res = await _protectedFetch(`${uri_tag}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updateObject),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  deleteTag: async (id: string, deletedBy: string) => {
    const res = await _protectedFetch(`${uri_tag}/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ deletedBy }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  deleteManyTags: async (ids: string, deletedBy: string) => {
    const res = await _protectedFetch(uri_tag_bulk, {
      method: 'DELETE',
      body: JSON.stringify({ ids, deletedBy }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  createTag: async (tagObject: Tag) => {
    const res = await _protectedFetch(uri_tag, {
      method: 'POST',
      body: JSON.stringify(tagObject),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  readAllFish: async (query?: Record<string, any>): Promise<Fish[]> => {
    const url = buildUrl(uri_fish, { 'page[size]': '-1', ...query });
    const res = await _protectedFetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    try {
      const data = await res.json();
      return data;
    } catch (error) {
      console.error(error);
      return [];
    }
  },
  getMembers: async (searchParams = {}) => {
    try {
      const queryParams = new URLSearchParams(searchParams).toString();
      const url = queryParams ? `${uri_fish}?${queryParams}` : `${uri_fish}`;

      const res = await _protectedFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return await res.json();
    } catch (error) {
      console.error('Error getting members by ID:', error);
    }
  },
  getUsers: async (searchParams = {}): Promise<PaginatedResponse<User>> => {
    const queryParams = new URLSearchParams(searchParams).toString();
    const url = queryParams ? `${uri_users}?${queryParams}` : `${uri_users}`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return await res.json();
  },
  getMembersWithMeta: async (
    searchParams = {},
  ): Promise<PaginatedResponse<Fish>> => {
    const queryParams = new URLSearchParams(searchParams).toString();
    const url = queryParams
      ? `${uri_fish}/with-meta?${queryParams}`
      : `${uri_fish}/with-meta`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return await res.json();
  },
  getFishV2: async (
    searchParams: Record<string, string | number> = {},
  ): Promise<PaginatedResponse<FishV2[]>> => {
    const url = buildUrl(uri_fish_v2, searchParams);
    const res = await _protectedFetch(url.toString(), { method: 'GET' });
    if (!res.ok) {
      throw new Error(`GET /v2/fish failed with ${res.status}`);
    }
    return await res.json();
  },
  /** Downloads the CSV of every fish matching the list query (`q`, `filter[…]`, `sort`). */
  exportFishV2: async (searchParams: Record<string, string | number> = {}) => {
    const url = buildUrl(`${uri_fish_v2}/_export`, searchParams);
    const res = await _protectedFetch(url.toString(), { method: 'GET' });
    if (!res.ok) {
      throw new Error(`GET /v2/fish/_export failed with ${res.status}`);
    }
    const filename =
      res.headers.get('Content-Disposition')?.match(/filename="([^"]+)"/)?.[1] ??
      'members.csv';
    const href = URL.createObjectURL(await res.blob());
    const link = document.createElement('a');
    link.href = href;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(href);
  },
  readAllSegments: async () => {
    const res = await _protectedFetch(uri_segment, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const segments = await res.json();
    return segments;
  },
  getAllSegmentsWithQuery: async (searchParams) => {
    const queryParams = new URLSearchParams(searchParams).toString();

    const url = queryParams
      ? `${uri_segment}/hook?${queryParams}`
      : `${uri_segment}/hook`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return await res.json();
  },
  readSegmentById: async (id: string) => {
    const res = await _protectedFetch(`${uri_segment}/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const segment = await res.json();
    return segment;
  },
  updateSegment: async (id: string, updateObject: any) => {
    const res = await _protectedFetch(`${uri_segment}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updateObject),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  deleteSegment: async (id: string) => {
    const res = await _protectedFetch(`${uri_segment}/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  createSegment: async (segmentObject: any) => {
    const res = await _protectedFetch(uri_segment, {
      method: 'POST',
      body: JSON.stringify(segmentObject),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  readSegmentsForFish: async (fishId: string) => {
    const res = await _protectedFetch(`${uri_fish}/${fishId}/segment/derived`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const segments = await res.json();
    return segments;
  },
  readFishSegments: async (fishId: string) => {
    // Pulls from FishSegments table
    const res = await _protectedFetch(`${uri_fish}/${fishId}/segment`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const segments = await res.json();
    return segments;
  },
  getFishForSegmentConditions: async (conditions: any) => {
    const res = await _protectedFetch(
      `${uri_segment}/get-fish-for-segment-condition`,
      {
        method: 'POST',
        body: JSON.stringify({ conditions }),
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    const fish = await res.json();
    return fish;
  },
  readFishById: async (id: string, includePrivateMeta = false) => {
    const query = includePrivateMeta
      ? '?includeEnrichment=true&includeForms=true'
      : '';
    const res = await _protectedFetch(`${uri_fish}/${id}${query}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    try {
      const data = await res.json();
      return data;
    } catch (e) {
      throw new Error(`Error during readFishById ${e}`);
    }
  },
  updateFish: async (id: string, updateObject: any) => {
    const res = await _protectedFetch(`${uri_fish}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updateObject),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return res;
  },
  readFishTags: async (id: string) => {
    const res = await _protectedFetch(`${uri_fish}/${id}/tag`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return await res.json();
  },
  addTagToFish: async (id: string, tagId: string) => {
    const res = await _protectedFetch(`${uri_fish}/${id}/tag`, {
      method: 'POST',
      body: JSON.stringify({ tagId }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  removeTagFromFish: async (id: string, tagId: string) => {
    const res = await _protectedFetch(`${uri_fish}/${id}/tag/${tagId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  addManyTagsToFish: async (id: string, tagIds: string[]) => {
    const res = await _protectedFetch(`${uri_fish}/${id}/tag/bulk`, {
      method: 'POST',
      body: JSON.stringify({ tagIds }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  removeManyTagsFromFish: async (id: string, tagIds: string[]) => {
    const res = await _protectedFetch(`${uri_fish}/${id}/tag/bulk`, {
      method: 'DELETE',
      body: JSON.stringify({ tagIds }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  deleteManyFish: async (ids: string, deletedBy: string) => {
    const res = await _protectedFetch(uri_fish_bulk, {
      method: 'DELETE',
      body: JSON.stringify({ ids, deletedBy }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  readMergeTags: async (id: string) => {
    const url = id ? `${uri_merge_tags}?id=${id}` : uri_merge_tags;
    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  getMessageTemplatesHook: async (searchParams = {}) => {
    try {
      const queryParams = new URLSearchParams(searchParams).toString();

      const url = queryParams
        ? `${uri_message_template}/hook?${queryParams}`
        : `${uri_message_template}/hook`;

      const res = await _protectedFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return await res.json();
    } catch (error) {
      console.error('Error getting message templates:', error);
    }
  },
  /**
   * Quick messages for one inbox channel from `/message-template/hook`,
   * optionally matching `q` on name, body, or category (one OR group each),
   * ordered by category (uncategorised last) then name.
   */
  getQuickMessages: async ({
    method,
    q,
  }: {
    method: QuickMessageMethod;
    q?: string;
  }): Promise<{ data: QuickMessage[] }> => {
    const url = buildUrl(`${uri_message_template}/hook`, {
      quickMessage: true,
      'filter[method]': method,
      ...(q && {
        'filter[0][name]': `*${q}*`,
        'filter[1][messageBody]': `*${q}*`,
        'filter[2][category]': `*${q}*`,
      }),
      sort: 'category,name',
      'page[size]': QUICK_MESSAGE_RESULT_LIMIT,
    });
    const res = await _protectedFetch(url.toString(), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      throw new Error('Unable to load quick messages');
    }
    return await res.json();
  },
  getMessageTemplateCategories: async (): Promise<{ data: string[] }> => {
    const res = await _protectedFetch(`${uri_message_template}/_categories`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      throw new Error('Unable to load quick message categories');
    }
    return await res.json();
  },
  /** `quickMessage` limits the list to quick messages (true) or to the other templates (false). */
  getMessageTemplates: async (query: { quickMessage?: boolean } = {}) => {
    const url = buildUrl(
      uri_message_template,
      query.quickMessage === undefined ? {} : { quickMessage: query.quickMessage },
    );
    const res = await _protectedFetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res.json();
  },
  getMessageTemplate: async (id: string) => {
    const res = await _protectedFetch(`${uri_message_template}/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res.json();
  },
  createUser: async (userObj) => {
    const { name, email, password, phoneNumber, roles } = userObj;
    const res = await _protectedFetch(uri_users, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        'Accept': 'application/vnd.api+json',
      },
      body: JSON.stringify({
        email,
        password,
        phoneNumber,
        name,
        roles,
      }),
    });
    return res.json();
  },
  readUserRoles: async () => {
    const res = await _protectedFetch(uri_user_roles, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res.json();
  },
  getUserRoles: async (
    searchParams = {},
  ): Promise<PaginatedResponse<UserRole[]>> => {
    const queryParams = new URLSearchParams(searchParams).toString();
    const url = queryParams
      ? `${uri_user_roles}?${queryParams}`
      : `${uri_user_roles}`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return await res.json();
  },
  readUserRole: async (id: string) => {
    const res = await _protectedFetch(`${uri_user_roles}/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res.json();
  },
  /** Fish roles (member roles) — options for content showFor gating. */
  getFishRoles: async (): Promise<{ id: string; name: string }[]> => {
    const res = await _protectedFetch(uri_fish_roles, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res.json();
  },
  getPermissions: async (
    searchParams = {},
  ): Promise<PaginatedResponse<Permissions>> => {
    const queryParams = new URLSearchParams(searchParams).toString();
    const url = queryParams
      ? `${uri_permissions}?${queryParams}`
      : `${uri_permissions}`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return await res.json();
  },
  getUserPermissions: async (userId: string) => {
    const res = await _protectedFetch(`${uri_users}/${userId}/permissions`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res.json();
  },
  getUserAccess: async (searchParams = {}) => {
    try {
      const queryParams = new URLSearchParams(searchParams).toString();

      const url = `${uri_user_access}?${queryParams}`;

      const res = await _protectedFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return await res.json();
    } catch (error) {
      console.error('Error getting user access:', error);
    }
  },
  createUserRole: async (name: string, description: string) => {
    const userRoleObj = { name, description };
    const res = await _protectedFetch(uri_user_roles, {
      method: 'POST',
      body: JSON.stringify(userRoleObj),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res.json();
  },
  updateUserRole: async (id: string, updateData: any) => {
    const res = await _protectedFetch(`${uri_user_roles}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res.json();
  },
  deleteUserRole: async (id: string) => {
    const res = await _protectedFetch(`${uri_user_roles}/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res.json();
  },
  addUserToRole: async (roleId: string, userId: string) => {
    const res = await _protectedFetch(
      `${uri_user_roles}/${roleId}/user/${userId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    return res.json();
  },
  removeUserFromRole: async (roleId: string, userId: string) => {
    const res = await _protectedFetch(
      `${uri_user_roles}/${roleId}/user/${userId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    return res.json();
  },
  addPermissionToRole: async (
    roleId: string,
    permissionId: string,
    targetType: string,
    targetId: string,
  ) => {
    const permissionConfig = { targetType, targetId };
    const res = await _protectedFetch(
      `${uri_user_roles}/${roleId}/permission/${permissionId}`,
      {
        method: 'POST',
        body: JSON.stringify(permissionConfig),
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    return res.json();
  },
  updateUserRolePermission: async (
    roleId: string,
    permissionId: string,
    targetType: string,
    targetId: string,
  ) => {
    const permissionConfig = { targetType, targetId };
    const res = await _protectedFetch(
      `${uri_user_roles}/${roleId}/permission/${permissionId}`,
      {
        method: 'PUT',
        body: JSON.stringify(permissionConfig),
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    return res.json();
  },
  removePermissionFromRole: async (roleId: string, permissionId: string) => {
    const res = await _protectedFetch(
      `${uri_user_roles}/${roleId}/permission/${permissionId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    return res.json();
  },
  handleUserRolePermissions: async (
    userRoleId: string,
    permissions: CreateUserRolePermission[],
  ) => {
    const updates = {
      userRoleId,
      permissions,
    };

    const res = await _protectedFetch(
      `${uri_user_roles}/handle-user-role-permissions`,
      {
        method: 'POST',
        body: JSON.stringify(updates),
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    return await res.json();
  },
  getAllInboxes: async (searchParams) => {
    const queryParams = new URLSearchParams(searchParams).toString();

    const url = queryParams
      ? `${uri_inbox}/hook?${queryParams}`
      : `${uri_inbox}/hook`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return await res.json();
  },
  getInboxManagement: async () => {
    const res = await _protectedFetch(`${uri_inbox}/management`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return await res.json();
  },
  createInbox: async (payload: {
    inboxType: string;
    phoneNumber?: string;
    email?: string;
    userId?: string;
  }) => {
    const res = await _protectedFetch(uri_inbox, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.message || 'Failed to create inbox');
    }
    return await res.json();
  },
  getPhoneNumbers: async () => {
    const res = await _protectedFetch(uri_phone_numbers, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.message || 'Failed to fetch phone numbers');
    }
    return await res.json();
  },
  registerPhoneNumber: async (payload: {
    phoneNumber: string;
    providerId?: string;
    kind?: 'DEDICATED' | 'POOL';
    region?: string;
  }) => {
    const res = await _protectedFetch(`${uri_phone_numbers}/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.message || 'Failed to register phone number');
    }
    return await res.json();
  },
  assignPhoneNumber: async (id: string, inboxId: string) => {
    const res = await _protectedFetch(`${uri_phone_numbers}/${id}/assign`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ inboxId }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.message || 'Failed to assign phone number');
    }
    return await res.json();
  },
  unassignPhoneNumber: async (id: string) => {
    const res = await _protectedFetch(`${uri_phone_numbers}/${id}/unassign`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.message || 'Failed to unassign phone number');
    }
    return await res.json();
  },
  getAttachedInboxTypes: async (stageTypeId: string): Promise<AttachedInboxTypeOption[]> => {
    const res = await _protectedFetch(`${uri_attached_inbox_types}?id=${stageTypeId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.message || 'Failed to fetch attached inbox types');
    }
    return await res.json();
  },
  getUserFields: async (stageTypeId: string) => {
    const res = await _protectedFetch(`${uri_user_fields}?id=${stageTypeId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.message || 'Failed to fetch user fields');
    }
    return await res.json();
  },
  readInbox: async (id: string) => {
    const res = await _protectedFetch(`${uri_inbox}/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res.json();
  },
  readAllInboxes: async () => {
    const res = await _protectedFetch(uri_inbox, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) {
      throw new Error(`Failed to fetch inboxes: ${res.status}`);
    }
    return res.json();
  },
  readInboxSMSThreads: async (inboxId: string) => {
    const res = await _protectedFetch(`${uri_sms_threads}/inbox/${inboxId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  createMessageTemplate: async (templateData: any) => {
    const res = await _protectedFetch(uri_message_template, {
      method: 'POST',
      body: JSON.stringify(templateData),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  updateMessageTemplate: async (templateId: string, updatedData: any) => {
    const res = await _protectedFetch(`${uri_message_template}/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(updatedData),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  deleteMessageTemplate: async (templateId: string) => {
    const res = await _protectedFetch(`${uri_message_template}/${templateId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  readSmsThread: async (recipient: string) => {
    const res = await _protectedFetch(`${uri_sms_threads}/${recipient}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  readAllFishSMSThreads: async () => {
    const res = await _protectedFetch(uri_sms_threads, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  getOmniDashboard: async (dashObj) => {
    const { id, name } = dashObj;
    const url = id
      ? `${uri_omni_dashboard}?id=${id}`
      : `${uri_omni_dashboard}?name=${name}`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  getOmniAgent: async () => {
    const url = `${uri_omni_dashboard}/agent`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  getAllTriggers: async () => {
    const res = await _protectedFetch(`${uri_triggers}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  getFlowTriggers: async (id: string) => {
    const res = await _protectedFetch(`${uri_get_flow_triggers}?id=${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  getFlowStageTriggers: async (id: string) => {
    const res = await _protectedFetch(
      `${uri_get_flow_stage_triggers}?id=${id}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    return res;
  },
  deleteTriggers: async (ids: string[], deletedBy: string) => {
    const res = await _protectedFetch(`${uri_triggers}`, {
      method: 'DELETE',
      body: JSON.stringify({ ids: ids, deletedBy: deletedBy }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  toggleTriggerActive: async (ids: string[], status: any) => {
    const res = await _protectedFetch(`${uri_toggle_trigger_active}`, {
      method: 'PUT',
      body: JSON.stringify({ ids: ids, status: status }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  createTrigger: async (triggerObj: any) => {
    const res = await _protectedFetch(`${uri_triggers}`, {
      method: 'POST',
      body: JSON.stringify(triggerObj),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  updateTrigger: async (triggerObj: any) => {
    const res = await _protectedFetch(`${uri_triggers}`, {
      method: 'PUT',
      body: JSON.stringify(triggerObj),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  getSubscriptionTiers: async () => {
    const res = await _protectedFetch(`${uri_get_subscription_tiers}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  getSubscriptionTiersWithMeta: async (
    query: Record<string, any> = {},
  ): Promise<PaginatedResponse<SubscriptionTier[]>> => {
    const url = buildUrl(uri_get_subscription_tiers_with_meta, query);

    const res = await _protectedFetch(url.toString(), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    return await res.json();
  },
  getSubscriptionTierById: async (id: string) => {
    const res = await _protectedFetch(
      `${uri_get_subscription_tier_by_id}/${id}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    if (!res.ok) {
      throw new Error(`Failed to load Subscription Tier (${res.status})`);
    }
    return await res.json();
  },
  getSubscriptionById: async (
    id: string,
    params: { include?: string } = {},
  ) => {
    const search = new URLSearchParams();
    if (params.include) search.set('include', params.include);

    const qs = search.toString();
    const url = `${uri_get_subscription}/${id}${qs ? `?${qs}` : ''}`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    return await res.json();
  },
  getPurchaseById: async (
    id: string,
    query?: Record<string, unknown>,
  ): Promise<Purchase> => {
    const url = buildUrl(`${uri_get_purchase_by_id}/${id}`, { ...query });
    const res = await _protectedFetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res.json();
  },
  runScript: async (script: string) => {
    const res = await _protectedFetch(uri_run_script, {
      method: 'POST',
      body: JSON.stringify({ script: script }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return res;
  },
  getNextQuarterlySpend: async (subscriptionId: string) => {
    const res = await _protectedFetch(
      `${uri_get_next_quarterly_spend}/${subscriptionId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    return res;
  },

  getFieldOptions: async () => {
    const res = await _protectedFetch(
      `${uri_field_options}wallet-adjustment-reasons`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    return await res.json();
  },

  adjustWalletBalance: async (
    id: string,
    amount: number,
    dry?: boolean,
    reason?: string,
    notes?: string,
  ) => {
    if (dry === undefined) {
      dry = false;
    }

    const payload: {
      amount: number;
      reason?: string;
      notes?: string;
    } = {
      amount: amount,
    };

    if (reason) {
      payload.reason = reason;
    }

    if (notes) {
      payload.notes = notes;
    }

    const res = await _protectedFetch(
      `${uri_wallet}/${id}/_adjust?dry=${dry}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      },
    );
    return res;
  },

  sendOtp: async (phoneNumber: string) => {
    const res = await fetch(uri_send_otp, {
      method: 'POST',
      body: JSON.stringify({ phoneNumber }),
      headers: {
        'Content-Type': 'application/json',
        'tenant-id': requireTenantId(),
      },
    });
    return res;
  },

  verifyOtp: async (phoneNumber: string, otp: string) => {
    const res = await fetch(uri_verify_user_otp, {
      method: 'POST',
      body: JSON.stringify({ phoneNumber, otp }),
      headers: {
        'Content-Type': 'application/json',
        'tenant-id': requireTenantId(),
      },
    });
    return res;
  },

  getActivity: async (fishId: string, cancelled?: boolean) => {
    const queryParams = new URLSearchParams();

    if (!fishId) {
      console.error('Fish ID Not Found');
      throw new Error('Fish ID Not Found');
    }

    if (cancelled) {
      queryParams.append('filter[cancelled]', 'true');
    }

    const queryString = queryParams.toString();
    const uri = uri_activity.replace(':fishId', fishId);
    const url = queryString ? `${uri}?${queryString}` : uri;

    // Make the protected fetch request
    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return res;
  },

  _getPurchases: async (
    fishId: string,
    productDescription: string,
    status: string,
  ) => {
    // Build the query string with optional parameters
    const queryParams = new URLSearchParams();

    if (fishId) {
      queryParams.append('fishId', fishId);
    }

    if (productDescription) {
      queryParams.append('productDescription', productDescription);
    }

    if (status) {
      queryParams.append('status', status);
    }

    // Convert query params to string and add to URL if any params exist
    const queryString = queryParams.toString();
    const url = queryString ? `${uri_purchases}?${queryString}` : uri_purchases;

    // Make the protected fetch request
    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return res;
  },
  getPurchases: async (
    query?: Record<string, unknown>,
  ): Promise<PaginatedResponse<Purchase[]>> => {
    const url = buildUrl(uri_purchases, { ...query });
    const res = await _protectedFetch(url.toString(), {
      method: 'GET',
    });

    return res.json();
  },
  getRestaurant: async (restaurantId: string) => {
    if (!restaurantId) {
      throw new Error('Missing restaurantId in service');
    }

    const res = await _protectedFetch(
      `${uri_get_restaurants}/${restaurantId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    return res;
  },
  getRestaurants: async (query?: GetRestaurantsQuery) => {
    const searchParams = new URLSearchParams();

    if (query?.name) searchParams.set('name', query.name);
    if (query?.guid) searchParams.set('guid', query.guid);
    if (query?.managementGroupGuid) {
      searchParams.set('managementGroupGuid', query.managementGroupGuid);
    }

    const qs = searchParams.toString();
    const url = qs ? `${uri_get_restaurants}?${qs}` : uri_get_restaurants;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      throw new Error(`Error fetching restaurants: ${res.statusText}`);
    }

    return await res.json();
  },
  getRegistrationCodeById: async (id: string): Promise<RegistrationCode> => {
    const res = await _protectedFetch(
      `${uri_get_registration_code_by_id}/${id}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw Object.assign(
        new Error(
          errorData.message || `Error loading invite code (${res.status})`,
        ),
        { status: res.status },
      );
    }

    return await res.json();
  },
  getAllRegistrationCodes: async (
    query: Record<string, any> = {},
  ): Promise<PaginatedResponse<RegistrationCode[]>> => {
    const url = buildUrl(uri_get_all_registration_codes, query);

    const res = await _protectedFetch(url.toString(), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    return await res.json();
  },
  createRegistrationCode: async (
    payload: CreateRegistrationCodePayload,
  ): Promise<RegistrationCode> => {
    const res = await _protectedFetch(uri_registration_code, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw Object.assign(
        new Error(
          errorData.message || `Error creating invite code (${res.status})`,
        ),
        { status: res.status, details: errorData.details },
      );
    }

    return await res.json();
  },
  updateRegistrationCode: async (
    id: string,
    payload: UpdateRegistrationCodePayload,
  ): Promise<RegistrationCode> => {
    const res = await _protectedFetch(`${uri_registration_code}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw Object.assign(
        new Error(
          errorData.message || `Error updating invite code (${res.status})`,
        ),
        { status: res.status, details: errorData.details },
      );
    }

    return await res.json();
  },
  deleteRegistrationCode: async (id: string): Promise<RegistrationCode> => {
    const res = await _protectedFetch(`${uri_registration_code}/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw Object.assign(
        new Error(
          errorData.message || `Error deleting invite code (${res.status})`,
        ),
        { status: res.status },
      );
    }

    return await res.json();
  },
  getInviteCodeUsage: async (id: string): Promise<RegistrationCodeUsage> => {
    const res = await _protectedFetch(`${uri_registration_code}/${id}/usage`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw Object.assign(
        new Error(
          errorData.message || `Error loading invite code usage (${res.status})`,
        ),
        { status: res.status },
      );
    }

    return await res.json();
  },
  getInviteCodeSubscriptions: async (
    id: string,
    query: Record<string, any> = {},
  ): Promise<PaginatedResponse<RegistrationCodeSubscription[]>> => {
    const url = buildUrl(`${uri_registration_code}/${id}/subscriptions`, query);

    const res = await _protectedFetch(url.toString(), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw Object.assign(
        new Error(
          errorData.message ||
            `Error loading invite code signups (${res.status})`,
        ),
        { status: res.status },
      );
    }

    return await res.json();
  },
  previewInviteCode: async (
    payload: PreviewRegistrationCodePayload,
  ): Promise<RegistrationCodePreview> => {
    const res = await _protectedFetch(`${uri_registration_code}/_preview`, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw Object.assign(
        new Error(
          errorData.message ||
            `Error previewing invite code (${res.status})`,
        ),
        { status: res.status },
      );
    }

    return await res.json();
  },
  uploadFishProfileImage: async (fishId: string, formData: FormData) => {
    const res = await _protectedFetch(`${uri_fish}/${fishId}/assets`, {
      method: 'POST',
      body: formData,
    });
    return res;
  },
  setAuthTokens: (tokens: any) => {
    const { access, refresh } = tokens;
    // Use admin-scoped cookie names so they don't collide with membership's
    // `at`/`rt` cookies when both apps share a domain in dev (localhost) or
    // sub-domain in prod. A collision causes the membership app to send the
    // admin's JWT to the API, which fails Auth0 JWKS validation.
    cookies.set('at-admin', access.token, { expires: access.expires });
    cookies.set('rt-admin', refresh.token, { expires: refresh.expires });
    // Clean up any stale collision-prone cookies from before this rename.
    cookies.erase('at');
    cookies.erase('rt');
  },
  getAuthTokens: () => {
    const accessToken = cookies.get('at-admin');
    const refreshToken = cookies.get('rt-admin');
    return {
      accessToken,
      refreshToken,
    };
  },
  clearAuthTokens: () => {
    cookies.erase('at-admin');
    cookies.erase('rt-admin');
    cookies.erase('at');
    cookies.erase('rt');
  },
  getPaymentSources: async (
    fishId: string,
    toastRestaurantGuid?: string,
  ): Promise<PaymentSources> => {
    const res = await _protectedFetch(
      `${uri_fish}/${fishId}/payment-sources`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
      toastRestaurantGuid,
    );
    if (!res.ok) {
      throw new Error(
        `GET /fish/${fishId}/payment-sources failed with ${res.status}`,
      );
    }
    return await res.json();
  },
  createPaymentIntent: async (
    fishDetails: {
      fName: string;
      lName: string;
      email: string;
      phoneNumber: string;
    },
    order: Order,
    productDescription: string,
    toastRestaurantGuid: string,
    cardFishId?: string,
  ): Promise<PaymentIntentCreateResponse | undefined> => {
    try {
      const res = await _protectedFetch(
        uri_toast_payment_intents,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            fishDetails,
            order,
            productDescription,
            cardFishId,
          }),
        },
        toastRestaurantGuid,
      );
      return await res.json();
    } catch (error) {
      console.error('Error creating payment intent:', error);
    }
  },
  confirmPaymentIntent: async (
    paymentIntentId: string,
    cardDetails: ConfirmPaymentIntentRequest,
    toastRestaurantGuid: string,
  ): Promise<ConfirmPaymentIntentResponse | undefined> => {
    try {
      const res = await _protectedFetch(
        `${uri_toast_payment_intents}/${paymentIntentId}/confirm`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ cardDetails }),
        },
        toastRestaurantGuid,
      );
      return await res.json();
    } catch (error) {
      console.error('Error confirming payment intent:', error);
    }
  },
  updatePaymentIntent: async (
    paymentIntentId: string,
    updateData: UpdatePaymentIntentRequest,
    toastRestaurantGuid: string,
  ): Promise<UpdatePaymentIntentResponse | undefined> => {
    try {
      const res = await _protectedFetch(
        `${uri_toast_payment_intents}/${paymentIntentId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData),
        },
        toastRestaurantGuid,
      );
      return await res.json();
    } catch (error) {
      console.error('Error updating payment intent:', error);
    }
  },
  getMenus: async (
    restaurantGuid: string,
    query?: { name?: string; guid?: string },
  ): Promise<MenusResponse | undefined> => {
    try {
      const queryString = query
        ? `?${new URLSearchParams(
            Object.entries(query).reduce(
              (acc, [key, value]) => {
                if (value) acc[key] = value;
                return acc;
              },
              {} as Record<string, string>,
            ),
          ).toString()}`
        : '';

      const res = await _protectedFetch(
        `${uri_toast_menus}${queryString}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
        restaurantGuid,
      );
      return await res.json();
    } catch (error) {
      console.error('Error getting menus:', error);
      throw error;
    }
  },
  getMenu: async (
    restaurantGuid: string,
    menuId: string,
  ): Promise<Menu | undefined> => {
    try {
      const res = await _protectedFetch(
        `${uri_toast_menus}/${menuId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
        restaurantGuid,
      );
      return await res.json();
    } catch (error) {
      console.error('Error getting menu by ID:', error);
    }
  },
  getServiceCharges: async (
    restaurantGuid: string,
    query?: { name?: string },
  ): Promise<ServiceCharge[] | undefined> => {
    try {
      const queryString = query
        ? `?${new URLSearchParams(
            Object.entries(query).reduce(
              (acc, [key, value]) => {
                if (value) acc[key] = value;
                return acc;
              },
              {} as Record<string, string>,
            ),
          ).toString()}`
        : '';

      const res = await _protectedFetch(
        `${uri_toast_service_charges}${queryString}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
        restaurantGuid,
      );
      return await res.json();
    } catch (error) {
      console.error('Error getting service charges:', error);
      throw error;
    }
  },
  getDiningOptions: async (
    restaurantGuid: string,
    query?: { name?: string },
  ): Promise<DiningOption[] | undefined> => {
    try {
      const queryString = query
        ? `?${new URLSearchParams(
            Object.entries(query).reduce(
              (acc, [key, value]) => {
                if (value) acc[key] = value;
                return acc;
              },
              {} as Record<string, string>,
            ),
          ).toString()}`
        : '';

      const res = await _protectedFetch(
        `${uri_toast_dining_options}${queryString}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
        restaurantGuid,
      );
      return await res.json();
    } catch (error) {
      console.error('Error getting dining options:', error);
      throw error;
    }
  },
  postOrder: async (
    order: Order,
    purchaseId: string,
    toastRestaurantGuid: string,
  ): Promise<OrderResponse | undefined> => {
    try {
      const res = await _protectedFetch(
        uri_toast_orders,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ order, purchaseId }),
        },
        toastRestaurantGuid,
      );
      return await res.json();
    } catch (error) {
      console.error('Error posting order:', error);
    }
  },

  // ===== Toast GUIDs =====
  getToastGuids: async (
    searchParams: Record<string, string> = {},
  ): Promise<{ data: ToastGuid[]; meta?: { total: number } }> => {
    const queryParams = new URLSearchParams(searchParams).toString();
    const url = queryParams
      ? `${uri_toast_guids}?${queryParams}`
      : uri_toast_guids;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return await res.json();
  },

  getToastGuid: async (id: string): Promise<ToastGuid> => {
    const res = await _protectedFetch(`${uri_toast_guids}/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) {
      throw new Error(`Failed to load Toast GUID (${res.status})`);
    }
    const json = await res.json();
    return json.data ?? json;
  },

  createToastGuid: async (
    payload: CreateToastGuidPayload,
  ): Promise<ToastGuid> => {
    const res = await _protectedFetch(uri_toast_guids, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Error creating toast guid (${res.status})`,
      );
    }

    return await res.json();
  },

  updateToastGuid: async (
    id: string,
    payload: UpdateToastGuidPayload,
  ): Promise<ToastGuid> => {
    const res = await _protectedFetch(`${uri_toast_guids}/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Error updating toast guid (${res.status})`,
      );
    }

    return await res.json();
  },

  deleteToastGuid: async (id: string): Promise<void> => {
    const res = await _protectedFetch(`${uri_toast_guids}/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Error deleting toast guid (${res.status})`,
      );
    }
  },

  // ===== Toast GUID Groups =====
  getToastGuidGroups: async (
    searchParams: Record<string, string> = {},
  ): Promise<{ data: ToastGuidGroup[]; meta?: { total: number } }> => {
    const queryParams = new URLSearchParams(searchParams).toString();
    const url = queryParams
      ? `${uri_toast_guid_groups}?${queryParams}`
      : uri_toast_guid_groups;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return await res.json();
  },

  getToastGuidGroup: async (
    id: string,
    params: { include?: string } = {},
  ): Promise<ToastGuidGroup> => {
    const search = new URLSearchParams();
    if (params.include) search.set('include', params.include);

    const qs = search.toString();
    const url = `${uri_toast_guid_groups}/${id}${qs ? `?${qs}` : ''}`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) {
      throw new Error(`Failed to load Toast GUID Group (${res.status})`);
    }
    const json = await res.json();
    return json.data ?? json;
  },

  createToastGuidGroup: async (
    payload: CreateToastGuidGroupPayload,
  ): Promise<ToastGuidGroup> => {
    const res = await _protectedFetch(uri_toast_guid_groups, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Error creating toast guid group (${res.status})`,
      );
    }

    return await res.json();
  },

  updateToastGuidGroup: async (
    id: string,
    payload: UpdateToastGuidGroupPayload,
  ): Promise<ToastGuidGroup> => {
    const res = await _protectedFetch(`${uri_toast_guid_groups}/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Error updating toast guid group (${res.status})`,
      );
    }

    return await res.json();
  },

  deleteToastGuidGroup: async (id: string): Promise<void> => {
    const res = await _protectedFetch(`${uri_toast_guid_groups}/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Error deleting toast guid group (${res.status})`,
      );
    }
  },

  // ===== Toast GUID Settings =====
  getToastGuidSettings: async (
    searchParams: Record<string, string> = {},
  ): Promise<{ data: ToastGuidSetting[]; meta?: { total: number } }> => {
    const queryParams = new URLSearchParams(searchParams).toString();
    const url = queryParams
      ? `${uri_toast_guid_settings}?${queryParams}`
      : uri_toast_guid_settings;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return await res.json();
  },

  updateToastGuidSetting: async (
    id: string,
    payload: UpdateToastGuidSettingPayload,
  ): Promise<ToastGuidSetting> => {
    const res = await _protectedFetch(`${uri_toast_guid_settings}/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Error updating toast guid setting (${res.status})`,
      );
    }

    return await res.json();
  },

  // ===== Promo Codes =====
  getPromoCodes: async (
    searchParams: Record<string, string> = {},
  ): Promise<{ data: PromoCode[]; meta?: { total: number } }> => {
    const queryParams = new URLSearchParams(searchParams).toString();
    const url = queryParams
      ? `${uri_promo_codes}?${queryParams}`
      : uri_promo_codes;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return await res.json();
  },

  getPromoCode: async (
    id: string,
    params: { include?: string } = {},
  ): Promise<PromoCode> => {
    const search = new URLSearchParams();
    if (params.include) search.set('include', params.include);

    const qs = search.toString();
    const url = `${uri_promo_codes}/${id}${qs ? `?${qs}` : ''}`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) {
      throw new Error(`Failed to load Promo Code (${res.status})`);
    }
    const json = await res.json();
    const data = json.data ?? json;
    if (json.meta) {
      data.meta = json.meta;
    }
    return data;
  },

  createPromoCode: async (
    payload: CreatePromoCodePayload,
  ): Promise<PromoCode> => {
    const res = await _protectedFetch(uri_promo_codes, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Error creating promo code (${res.status})`,
      );
    }

    return await res.json();
  },

  updatePromoCode: async (
    id: string,
    payload: UpdatePromoCodePayload,
  ): Promise<PromoCode> => {
    const res = await _protectedFetch(`${uri_promo_codes}/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Error updating promo code (${res.status})`,
      );
    }

    return await res.json();
  },

  deletePromoCode: async (id: string): Promise<void> => {
    const res = await _protectedFetch(`${uri_promo_codes}/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Error deleting promo code (${res.status})`,
      );
    }
  },

  // ===== Partner Referral Codes =====
  getPartnerReferralCodes: async (
    query: Record<string, any> = {},
  ): Promise<PaginatedResponse<PartnerReferralCode[]>> => {
    const url = buildUrl(uri_partner_referral_codes, query);

    const res = await _protectedFetch(url.toString(), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw Object.assign(
        new Error(
          errorData.message ||
            `Error loading partner referral codes (${res.status})`,
        ),
        { status: res.status },
      );
    }

    return await res.json();
  },

  getPartnerReferralCodeById: async (
    id: string,
  ): Promise<PartnerReferralCode> => {
    const res = await _protectedFetch(`${uri_partner_referral_codes}/${id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw Object.assign(
        new Error(
          errorData.message ||
            `Error loading partner referral code (${res.status})`,
        ),
        { status: res.status },
      );
    }

    return await res.json();
  },

  getPartnerReferralCodeSubscriptions: async (
    id: string,
    query: Record<string, any> = {},
  ): Promise<PaginatedResponse<PartnerReferralCodeSubscription[]>> => {
    const url = buildUrl(
      `${uri_partner_referral_codes}/${id}/subscriptions`,
      query,
    );

    const res = await _protectedFetch(url.toString(), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw Object.assign(
        new Error(
          errorData.message ||
            `Error loading partner referral code signups (${res.status})`,
        ),
        { status: res.status },
      );
    }

    return await res.json();
  },

  createPartnerReferralCode: async (
    payload: CreatePartnerReferralCodePayload,
  ): Promise<PartnerReferralCode> => {
    const res = await _protectedFetch(uri_partner_referral_codes, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw Object.assign(
        new Error(
          errorData.message ||
            `Error creating partner referral code (${res.status})`,
        ),
        { status: res.status },
      );
    }

    return await res.json();
  },

  updatePartnerReferralCode: async (
    id: string,
    payload: UpdatePartnerReferralCodePayload,
  ): Promise<PartnerReferralCode> => {
    const res = await _protectedFetch(`${uri_partner_referral_codes}/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw Object.assign(
        new Error(
          errorData.message ||
            `Error updating partner referral code (${res.status})`,
        ),
        { status: res.status },
      );
    }

    return await res.json();
  },

  deletePartnerReferralCode: async (
    id: string,
  ): Promise<PartnerReferralCode> => {
    const res = await _protectedFetch(`${uri_partner_referral_codes}/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw Object.assign(
        new Error(
          errorData.message ||
            `Error deleting partner referral code (${res.status})`,
        ),
        { status: res.status },
      );
    }

    return await res.json();
  },

  // ===== Redemptions =====
  getRedemptions: async (
    searchParams: Record<string, string> = {},
  ): Promise<{ data: Redemption[]; meta?: { total: number } }> => {
    const queryParams = new URLSearchParams(searchParams).toString();
    const url = queryParams
      ? `${uri_redemptions}?${queryParams}`
      : uri_redemptions;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to load redemptions (${res.status})`,
      );
    }

    return await res.json();
  },

  getSharedSubscription: async (fishId: string, subscriptionId: string) => {
    try {
      const queryParams = new URLSearchParams({
        'filter[id]': fishId,
      }).toString();

      const res = await _protectedFetch(
        `${uri_fish}/${fishId}/subscriptions/${subscriptionId}/sharedSubscriptions?${queryParams}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
      const data = await res.json();
      return data;
    } catch (error) {
      console.error('Error getting all shared subscriptions', error);
    }
  },
  updateSharedSubscription: async (
    fishId: string,
    subscriptionId: string,
    additionalFishId: string,
  ) => {
    try {
      const res = await _protectedFetch(
        `${uri_fish}/${fishId}/subscriptions/${subscriptionId}/sharedSubscriptions`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ additionalFishId }),
        },
      );
      const data = await res.json();
      return data;
    } catch (error) {
      console.error('Error updating shared subscription', error);
    }
  },
  updateSharedSubscriptionAccess: async (
    primaryFishId: string,
    subscriptionId: string,
    secondaryFishId: string,
    access: SharedSubscriptionAccessUpdate,
  ): Promise<SharedSubscriptionFish> => {
    const res = await _protectedFetch(
      `${uri_fish}/${primaryFishId}/subscriptions/${subscriptionId}/sharedSubscriptions/${secondaryFishId}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(access),
      },
    );
    if (!res.ok) {
      throw new Error(
        `PATCH shared subscription access failed with ${res.status}`,
      );
    }
    return await res.json();
  },
  deleteSharedSubscription: async (
    subscriptionId: string,
    fishId: string,
    userEmail: string,
  ) => {
    try {
      const res = await _protectedFetch(
        `${uri_fish}/${fishId}/subscriptions/${subscriptionId}/sharedSubscriptions`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userEmail }),
        },
      );
      const data = await res.json();
      return data;
    } catch (error) {
      console.error('Error deleting shared subscription', error);
    }
  },
  cancelSubscription: async (subscriptionId: string) => {
    try {
      const res = await _protectedFetch(
        `${uri_subscription}/${subscriptionId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
      const data = await res.json();
      return data;
    } catch (e) {
      console.error(`Error while cancelling subscription: ${e}`);
    }
  },
  getSchema: async (resource: string) => {
    const res = await _protectedFetch(`${DOMAIN}/${resource}/_schema`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const data = await res.json();

    return data?.meta?.schema || [];
  },
  getResourceFieldValues: async (
    resource: string,
    field: string,
    valuesUri?: string,
  ) => {
    const url = valuesUri
      ? `${DOMAIN}${valuesUri}`
      : `${DOMAIN}/${resource}/${field}/_values`;
    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return await res.json();
  },

  getVenue: async (
    venueId: string,
    params: GetVenueParams = {},
  ): Promise<Venue> => {
    const search = new URLSearchParams();
    if (params.include) search.set('include', params.include);
    if (params.toastInclude) search.set('toastInclude', params.toastInclude);

    const qs = search.toString();
    const url = `${uri_venues}/${venueId}${qs ? `?${qs}` : ''}`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`Failed to load venue (${res.status})`);
    }

    return (await res.json()) as Venue;
  },
  getVenues: async (params: GetVenuesParams = {}) => {
    if (!params) return '';

    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) return;

      // Everything must be a string for URLSearchParams
      searchParams.append(key, String(value));
    });

    const query = searchParams.toString();

    const res = await _protectedFetch(
      query ? `${uri_venues}?${query}` : uri_venues,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    if (!res.ok) {
      throw new Error(`Failed to load venues (${res.status})`);
    }

    const data = await res.json();
    return data; // <- this is what your React Query hook will see
  },
  createVenue: async (
    venue: CreateVenuePayload,
  ): Promise<Venue | undefined> => {
    try {
      const res = await _protectedFetch(uri_venues, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(venue),
      });

      // Optional: throw on non-2xx so callers can show an error toast, etc.
      if (!res.ok) {
        console.error('Error creating venue: HTTP', res.status);
        throw new Error(`Error creating venue (${res.status})`);
      }

      return (await res.json()) as Venue;
    } catch (error) {
      console.error('Error creating venue:', error);
    }
  },
  updateVenue: async (
    venueId: string,
    payload: UpdateVenuePayload,
  ): Promise<Venue> => {
    const res = await _protectedFetch(`${uri_venues}/${venueId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      console.error('Error updating venue: HTTP', res.status);
      throw new Error(`Error updating venue (${res.status})`);
    }

    return (await res.json()) as Venue;
  },
  assignVenueUser: async (
    venueId: string,
    userId: string,
    role: 'EVENT_MANAGER' = 'EVENT_MANAGER',
  ): Promise<VenueUserAssignment> => {
    const res = await _protectedFetch(`${uri_venues}/${venueId}/assignments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId, role }),
    });

    if (!res.ok) {
      console.error('Error assigning venue user: HTTP', res.status);
      throw new Error(`Error assigning venue user (${res.status})`);
    }

    return (await res.json()) as VenueUserAssignment;
  },
  removeVenueAssignment: async (
    venueId: string,
    assignmentId: string,
  ): Promise<void> => {
    const res = await _protectedFetch(
      `${uri_venues}/${venueId}/assignments/${assignmentId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    if (!res.ok) {
      console.error('Error removing venue assignment: HTTP', res.status);
      throw new Error(`Error removing venue assignment (${res.status})`);
    }
  },
  deleteVenue: async (venueId: string): Promise<void> => {
    const res = await _protectedFetch(`${uri_venues}/${venueId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return await res.json();
    if (!res.ok) {
      console.error('Error deleting venue: HTTP', res.status);
      throw new Error(`Error deleting venue (${res.status})`);
    }
  },
  getSpace: async (
    spaceId: string,
    params: GetSpaceParams = {},
  ): Promise<Space> => {
    const search = new URLSearchParams();
    if (params.include) search.set('include', params.include);

    const qs = search.toString();
    const url = `${uri_spaces}/${spaceId}${qs ? `?${qs}` : ''}`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`Failed to load space (${res.status})`);
    }

    return (await res.json()) as Space;
  },
  getSpaces: async (params: GetSpacesParams = {}) => {
    if (!params) return '';

    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) return;

      // Everything must be a string for URLSearchParams
      searchParams.append(key, String(value));
    });

    const query = searchParams.toString();

    const res = await _protectedFetch(
      query ? `${uri_spaces}?${query}` : uri_spaces,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    if (!res.ok) {
      throw new Error(`Failed to load spaces (${res.status})`);
    }

    const data = await res.json();
    return data;
  },
  createSpace: async (
    space: CreateSpacePayload,
  ): Promise<Space | undefined> => {
    try {
      const res = await _protectedFetch(uri_spaces, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(space),
      });

      if (!res.ok) {
        console.error('Error creating space: HTTP', res.status);
        throw new Error(`Error creating space (${res.status})`);
      }

      return (await res.json()) as Space;
    } catch (error) {
      console.error('Error creating space:', error);
    }
  },
  updateSpace: async (
    spaceId: string,
    payload: UpdateSpacePayload,
  ): Promise<Space> => {
    const res = await _protectedFetch(`${uri_spaces}/${spaceId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      console.error('Error updating space: HTTP', res.status);
      throw new Error(`Error updating space (${res.status})`);
    }

    return (await res.json()) as Space;
  },
  deleteSpace: async (spaceId: string): Promise<void> => {
    const res = await _protectedFetch(`${uri_spaces}/${spaceId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      console.error('Error deleting space: HTTP', res.status);
      throw new Error(`Error deleting space (${res.status})`);
    }
  },
  getEventBookingEvents: async (params: GetEventBookingEventsParams = {}) => {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.set(key, String(value));
      }
    });

    const query = searchParams.toString();
    const res = await _protectedFetch(
      query ? `${uri_event_booking_events}?${query}` : uri_event_booking_events,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      },
    );

    if (!res.ok) {
      throw new Error(`Failed to load event booking events (${res.status})`);
    }

    const data = await res.json();
    return data;
  },
  getEventBookingEvent: async (
    eventId: string,
    params: GetEventBookingEventParams = {},
  ): Promise<EventBookingEvent> => {
    const search = new URLSearchParams();
    if (params.include) search.set('include', params.include);
    const qs = search.toString();
    const url = `${uri_event_booking_events}/${eventId}${qs ? `?${qs}` : ''}`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`Failed to load event booking event (${res.status})`);
    }

    return (await res.json()) as EventBookingEvent;
  },
  deleteEventBookingEvent: async (eventId: string): Promise<void> => {
    const res = await _protectedFetch(
      `${uri_event_booking_events}/${eventId}`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      },
    );

    if (!res.ok) {
      console.error('Error deleting event booking event: HTTP', res.status);
      throw new Error(`Error deleting event booking event (${res.status})`);
    }
  },
  getEventBookingEventTypes: async () => {
    const res = await _protectedFetch(uri_event_booking_event_types, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`Failed to load event types (${res.status})`);
    }

    return await res.json();
  },
  getEventBookingStatuses: async () => {
    const res = await _protectedFetch(uri_event_booking_statuses, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`Failed to load event statuses (${res.status})`);
    }

    return await res.json();
  },
  createEventBookingEvent: async (
    payload: CreateEventBookingEventPayload,
  ): Promise<EventBookingEvent | undefined> => {
    try {
      const res = await _protectedFetch(uri_event_booking_events, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(`Error creating event (${res.status})`);
      }

      return (await res.json()) as EventBookingEvent;
    } catch (error) {
      console.error('Error creating event booking event:', error);
    }
  },
  getAssets: async (searchParams = {}) => {
    try {
      const queryParams = new URLSearchParams(searchParams).toString();
      const url = queryParams
        ? `${uri_assets}?${queryParams}`
        : `${uri_assets}`;

      const res = await _protectedFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return await res.json();
    } catch (error) {
      console.error('Error getting assets:', error);
    }
  },
  getAssetTags: async (searchParams = {}) => {
    try {
      const queryParams = new URLSearchParams(searchParams).toString();
      const url = queryParams
        ? `${uri_asset_tags}?${queryParams}`
        : `${uri_asset_tags}`;

      const res = await _protectedFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return await res.json();
    } catch (error) {
      console.error('Error getting asset tags:', error);
    }
  },
  deleteAssetTag: async (id: string) => {
    const res = await _protectedFetch(`${uri_asset_tags}/${id}`, {
      method: 'DELETE',
    });

    if (!res.ok) {
      const msg = await res.text().catch(() => res.statusText);
      throw new Error(msg || 'Failed to delete asset tag');
    }
  },
  updateAsset: async (id: string, payload: Record<string, any>) => {
    const res = await _protectedFetch(`${uri_assets}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      const msg = await res.text().catch(() => res.statusText);
      throw new Error(msg || 'Failed to update asset');
    }

    return res.json();
  },
  uploadImagesToOrg: async (
    orgId: string,
    formData: FormData,
    opts?: { signal?: AbortSignal },
  ) => {
    const res = await _protectedFetch(`${uri_images}/upload/${orgId}`, {
      method: 'POST',
      body: formData,
      signal: opts?.signal,
    });

    if (!res.ok) {
      const msg = await res.text().catch(() => res.statusText);
      const err: any = new Error(msg || 'Upload failed');
      err.status = res.status;
      throw err;
    }
    return res.json();
  },

  createTicketedEvent: async (eventData) => {
    const res = await _protectedFetch(uri_ticketed_events, {
      method: 'POST',
      body: JSON.stringify(eventData),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return res;
  },
  getTicketedEvents: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        queryParams.append(key, String(value));
      }
    });
    const queryString = queryParams.toString();
    const url = queryString
      ? `${uri_ticketed_events}?${queryString}`
      : `${uri_ticketed_events}`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`${res.status} ${res.statusText} :: ${body}`);
    }
    return res.json();
  },
  getTicketedEvent: async (
    eventId: string,
    params: GetTicketedEventParams = {},
  ) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        search.set(key, String(value));
      }
    });

    const qs = search.toString();
    const url = `${uri_ticketed_events}/${eventId}${qs ? `?${qs}` : ''}`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`Failed to load ticketed event (${res.status})`);
    }

    return await res.json();
  },
  updateTicketedEvent: async (
    eventId: string,
    payload: UpdateTicketedEventPayload,
  ) => {
    const res = await _protectedFetch(`${uri_ticketed_events}/${eventId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      console.error('Error updating ticketed event: HTTP', res.status);
      throw new Error(`Error updating ticketed event (${res.status})`);
    }

    return await res.json();
  },
  deleteTicketedEvent: async (
    eventId: string,
    payload: { userId?: string; fishId?: string },
  ) => {
    const res = await _protectedFetch(`${uri_ticketed_events}/${eventId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      console.error('Error deleting ticketed event: HTTP', res.status);
      throw new Error(`Error deleting ticketed event (${res.status})`);
    }
  },
  syncTicketTypes: async (
    eventId: string,
    ticketTypes: Array<{
      id?: string;
      name: string;
      description?: string | null;
      price: string;
      available: number;
      sortOrder: number;
    }>,
  ) => {
    const res = await _protectedFetch(
      `${uri_ticketed_events}/${eventId}/ticket-types`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticketTypes }),
      },
    );

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = new Error(
        body.message || `Error syncing ticket types (${res.status})`,
      );
      (err as any).status = res.status;
      (err as any).body = body;
      throw err;
    }

    return await res.json();
  },
  getTicketedEventPurchases: async (eventId: string) => {
    // Fetch the event with tickets and purchases included
    const search = new URLSearchParams();
    search.set(
      'include',
      'tickets.purchase.redemptions.promoCode,tickets.ticketType,tickets.fish.fishProfiles',
    );

    const url = `${uri_ticketed_events}/${eventId}?${search.toString()}`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      throw new Error(
        `Failed to load ticketed event purchases (${res.status})`,
      );
    }

    const event = await res.json();

    // Aggregate tickets by purchaseId
    if (!event.tickets || !Array.isArray(event.tickets)) {
      return [];
    }

    // Group tickets by purchaseId
    const purchaseMap = new Map<
      string,
      {
        purchase: any;
        tickets: any[];
      }
    >();

    event.tickets.forEach((ticket: any) => {
      if (ticket.purchaseId && ticket.purchase) {
        if (!purchaseMap.has(ticket.purchaseId)) {
          purchaseMap.set(ticket.purchaseId, {
            purchase: ticket.purchase,
            tickets: [],
          });
        }
        purchaseMap.get(ticket.purchaseId)!.tickets.push(ticket);
      }
    });

    // Convert map to array
    return Array.from(purchaseMap.values());
  },
  getTicketedEventTags: async () => {
    const res = await _protectedFetch(uri_ticketed_event_tags, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return await res.json();
  },
  createTicketedEventTag: async (tag: {
    name: string;
    description?: string | null;
  }) => {
    const res = await _protectedFetch(uri_ticketed_event_tags, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(tag),
    });

    // Read body once; some APIs return empty body on 204 etc.
    const text = await res.text();

    if (!res.ok) {
      let msg = res.statusText || 'Request failed';
      try {
        const errJson = text ? JSON.parse(text) : null;
        msg = errJson?.message || errJson?.error || msg;
      } catch {
        // ignore JSON parse error; keep fallback msg
      }
      throw new Error(`${res.status} ${msg}`);
    }

    // Parse JSON if present; some APIs return { data: ... }
    return text ? JSON.parse(text) : null;
  },
  getTicketedEventCategories: async (params = {}) => {
    const qs = buildJsonApiQuery(params);
    const url = qs
      ? `${uri_ticketed_event_categories}?${qs}`
      : uri_ticketed_event_categories;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`${res.status} ${res.statusText} :: ${body}`);
    }
    return res.json();
  },
  createTicketedEventCategory: async (category: {
    name: string;
    description?: string | null;
  }) => {
    const res = await _protectedFetch(uri_ticketed_event_categories, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(category),
    });

    // Read body once; some APIs return empty body on 204 etc.
    const text = await res.text();

    if (!res.ok) {
      let msg = res.statusText || 'Request failed';
      try {
        const errJson = text ? JSON.parse(text) : null;
        msg = errJson?.message || errJson?.error || msg;
      } catch {
        // ignore JSON parse error; keep fallback msg
      }
      throw new Error(`${res.status} ${msg}`);
    }

    // Parse JSON if present; some APIs return { data: ... }
    return text ? JSON.parse(text) : null;
  },
  getTicketedEventTickets: async (params: Record<string, unknown> = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        queryParams.append(key, String(value));
      }
    });
    const queryString = queryParams.toString();
    const url = queryString
      ? `${uri_ticketed_event_tickets}?${queryString}`
      : `${uri_ticketed_event_tickets}`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`${res.status} ${res.statusText} :: ${body}`);
    }
    return res.json();
  },

  // ===== Events (non-ticketed) =====
  getEvents: async (params: Record<string, unknown> = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        queryParams.append(key, String(value));
      }
    });
    const queryString = queryParams.toString();
    const url = queryString ? `${uri_events}?${queryString}` : `${uri_events}`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`${res.status} ${res.statusText} :: ${body}`);
    }
    return res.json();
  },
  getEvent: async (eventId: string, params: { include?: string } = {}) => {
    const search = new URLSearchParams();
    if (params.include) search.set('include', params.include);

    const qs = search.toString();
    const url = `${uri_events}/${eventId}${qs ? `?${qs}` : ''}`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`Failed to load event (${res.status})`);
    }

    return await res.json();
  },
  createEvent: async (eventData: Record<string, unknown>) => {
    const res = await _protectedFetch(uri_events, {
      method: 'POST',
      body: JSON.stringify(eventData),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return res;
  },
  updateEvent: async (eventId: string, payload: Record<string, unknown>) => {
    const res = await _protectedFetch(`${uri_events}/${eventId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      console.error('Error updating event: HTTP', res.status);
      throw new Error(`Error updating event (${res.status})`);
    }

    return await res.json();
  },
  deleteEvent: async (eventId: string) => {
    const res = await _protectedFetch(`${uri_events}/${eventId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`Failed to delete event (${res.status})`);
    }

    return res;
  },

  // ===== Organizers =====
  getOrganizers: async (params: Record<string, unknown> = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        queryParams.append(key, String(value));
      }
    });
    const queryString = queryParams.toString();
    const url = queryString
      ? `${uri_ticketed_event_organizers}?${queryString}`
      : `${uri_ticketed_event_organizers}`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`${res.status} ${res.statusText} :: ${body}`);
    }
    return res.json();
  },
  getOrganizer: async (organizerId: string) => {
    const url = `${uri_ticketed_event_organizers}/${organizerId}`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`Failed to load organizer (${res.status})`);
    }

    return await res.json();
  },
  createOrganizer: async (data: {
    name: string;
    email?: string | null;
    phone?: string | null;
    website?: string | null;
  }) => {
    const res = await _protectedFetch(uri_ticketed_event_organizers, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const text = await res.text();

    if (!res.ok) {
      let msg = res.statusText || 'Request failed';
      try {
        const errJson = text ? JSON.parse(text) : null;
        msg = errJson?.message || errJson?.error || msg;
      } catch {
        // ignore
      }
      throw new Error(`${res.status} ${msg}`);
    }

    return text ? JSON.parse(text) : null;
  },
  updateOrganizer: async (
    organizerId: string,
    data: {
      name?: string;
      email?: string | null;
      phone?: string | null;
      website?: string | null;
    },
  ) => {
    const res = await _protectedFetch(
      `${uri_ticketed_event_organizers}/${organizerId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      },
    );

    if (!res.ok) {
      throw new Error(`Error updating organizer (${res.status})`);
    }

    return await res.json();
  },
  deleteOrganizer: async (organizerId: string) => {
    const res = await _protectedFetch(
      `${uri_ticketed_event_organizers}/${organizerId}`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      },
    );

    if (!res.ok) {
      throw new Error(`Failed to delete organizer (${res.status})`);
    }

    return res;
  },

  // ===== Category & Tag Update =====
  updateTicketedEventCategory: async (
    categoryId: string,
    data: { name?: string; description?: string | null },
  ) => {
    const res = await _protectedFetch(
      `${uri_ticketed_event_categories}/${categoryId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      },
    );

    if (!res.ok) {
      throw new Error(`Error updating category (${res.status})`);
    }

    return await res.json();
  },
  updateTicketedEventTag: async (
    tagId: string,
    data: { name?: string; description?: string | null },
  ) => {
    const res = await _protectedFetch(`${uri_ticketed_event_tags}/${tagId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!res.ok) {
      throw new Error(`Error updating tag (${res.status})`);
    }

    return await res.json();
  },

  // ===== Messaging (multi-channel: SMS, APP, EMAIL) =====
  sendMessage: async (payload: {
    fromInboxId: string;
    toFishId: string;
    method: 'SMS' | 'APP' | 'EMAIL';
    messageBody: string;
    messageSubject?: string;
    threadId?: string;
  }) => {
    const res = await _protectedFetch(`${DOMAIN}/messaging/send`, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json' },
    });
    return res.json();
  },

  sendBulkMessage: async (payload: {
    fromInboxId: string;
    recipients: {
      fishIds?: string[];
      segmentIds?: string[];
      tagIds?: string[];
    };
    method: 'SMS' | 'APP' | 'EMAIL';
    messageBody: string;
    messageSubject?: string;
    filterSubscribed?: boolean;
  }) => {
    const res = await _protectedFetch(`${DOMAIN}/messaging/send-bulk`, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json' },
    });
    return res.json();
  },

  getEngagementStats: async (range: StatsRange): Promise<EngagementStats> => {
    const res = await _protectedFetch(
      `${DOMAIN}/messaging/stats?${new URLSearchParams(statsRangeParams(range))}`,
      { method: 'GET' },
    );
    const body = await res.json();
    if (!res.ok) {
      throw new Error(body?.message || `HTTP error! status: ${res.status}`);
    }
    return body.data;
  },

  getFirstTouchStats: async (
    flowId: string,
    range: StatsRange,
  ): Promise<FirstTouchStats> => {
    const res = await _protectedFetch(
      `${DOMAIN}/messaging/stats/first-touch?${new URLSearchParams({ flowId, ...statsRangeParams(range) })}`,
      { method: 'GET' },
    );
    const body = await res.json();
    if (!res.ok) {
      throw new Error(body?.message || `HTTP error! status: ${res.status}`);
    }
    return body.data;
  },

  getEmailThreads: async (searchParams = {}) => {
    try {
      const queryParams = new URLSearchParams(searchParams).toString();
      const url = queryParams
        ? `${uri_emailThread}?${queryParams}`
        : `${uri_emailThread}`;

      const res = await _protectedFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return await res.json();
    } catch (error) {
      console.error('Error getting Email Threads:', error);
    }
  },

  getEmailThread: async (id: string, searchParams = {}) => {
    const queryParams = new URLSearchParams(searchParams).toString();
    const url = queryParams
      ? `${uri_emailThread}/${id}?${queryParams}`
      : `${uri_emailThread}/${id}`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return await res.json();
  },

  getUnifiedThreads: async (inboxId: string, channel: string) => {
    const url = channel
      ? `${DOMAIN}/inbox/${inboxId}/threads?channel=${channel}`
      : `${DOMAIN}/inbox/${inboxId}/threads`;
    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    return res.json();
  },

  getBatchThreads: async (inboxIds: string[], channel: string) => {
    const res = await _protectedFetch(`${DOMAIN}/inbox/_threads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ inboxIds, channel }),
    });
    return res.json();
  },

  markMessagesAsRead: async (messageIds: string[]) => {
    if (!messageIds || messageIds.length === 0) return { count: 0 };
    const res = await _protectedFetch(`${DOMAIN}/inbound-messages/_bulk`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ids: messageIds,
        data: { read: true },
      }),
    });
    return res.json();
  },

  getUserInboxUnread: async (userId: string) => {
    const res = await _protectedFetch(
      `${DOMAIN}/users/${userId}/inbox/_unread`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      },
    );
    return res.json();
  },

  // ===== Souls =====
  getSouls: async (
    params: { parentSoulId?: string | null } = {},
  ): Promise<Soul[]> => {
    const search = new URLSearchParams();
    if (params.parentSoulId === null) {
      search.set('parentSoulId', 'null');
    } else if (typeof params.parentSoulId === 'string') {
      search.set('parentSoulId', params.parentSoulId);
    }
    const url = search.toString()
      ? `${uri_souls}?${search.toString()}`
      : uri_souls;
    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await res.json();
    return Array.isArray(json) ? json : (json.data ?? []);
  },
  getAiSnippetPreview: async (
    soulId: string,
    fishId: string,
    extra: { responseId?: string | null; purchaseId?: string | null } = {},
  ): Promise<{ text: string }> => {
    const res = await _protectedFetch(
      _getFullPath(DOMAIN, '/ai/snippets/preview'),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ soulId, fishId, ...extra }),
      },
    );
    if (!res.ok) {
      throw new Error(`Error previewing AI snippet (${res.status})`);
    }
    const json = await res.json();
    return json?.data ?? { text: '' };
  },
  getSoul: async (id: string): Promise<Soul> => {
    const res = await _protectedFetch(`${uri_souls}/${id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    return res.json();
  },
  getAiSuggestions: async (
    soulId: string,
    chatId?: string,
  ): Promise<AiSuggestion[]> => {
    const res = await _protectedFetch(`${uri_souls}/${soulId}/_suggest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ chatId }),
    });
    if (!res.ok) return [];
    const json = await res.json();
    return json?.data?.suggestions ?? [];
  },
  createSoul: async (payload: CreateSoulPayload): Promise<Soul> => {
    const res = await _protectedFetch(uri_souls, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      throw new Error(`Error creating soul (${res.status})`);
    }
    return res.json();
  },
  createActivity: async (payload: {
    data: {
      id: string;
      type: 'Booked Event' | 'Reservation' | 'Ticketed Event' | 'Hotel Stay';
      fishId: string;
      location?: string | null;
      space?: string | null;
      phoneNumber?: string | null;
      status?: string | null;
      amount?: number | null;
      bookDate?: string | null;
      activityDate?: string | null;
      createdDate?: string | null;
      updatedDate?: string | null;
      eventType?: string | null;
      typeMeta?: Record<string, unknown> | null;
      attachedInboxId?: string | null;
    };
    fireCreatedTrigger?: boolean;
    fireUpdatedTrigger?: boolean;
  }) => {
    const res = await _protectedFetch(`${DOMAIN}/activity`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      throw new Error(`Error creating activity (${res.status})`);
    }
    return res.json();
  },
  updateSoul: async (id: string, payload: UpdateSoulPayload): Promise<Soul> => {
    const res = await _protectedFetch(`${uri_souls}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      throw new Error(`Error updating soul (${res.status})`);
    }
    return res.json();
  },
  deleteSoul: async (id: string): Promise<void> => {
    const res = await _protectedFetch(`${uri_souls}/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      throw new Error(`Error deleting soul (${res.status})`);
    }
  },

  // ===== Agent desk (profile, operations, shift reports) =====
  uploadSoulImage: async (soulId: string, formData: FormData) => {
    const res = await _protectedFetch(`${uri_souls}/${soulId}/image`, {
      method: 'POST',
      body: formData,
    });
    if (!res.ok) throw new Error(`Error uploading image (${res.status})`);
    return res;
  },

  getAgentProfile: async (soulId: string): Promise<AgentProfile> => {
    const res = await _protectedFetch(`${uri_souls}/${soulId}/profile`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      throw new Error(`Error loading agent profile (${res.status})`);
    }
    const json = await res.json();
    return json.data ?? json;
  },

  getAgentProfiles: async (): Promise<AgentProfile[]> => {
    const res = await _protectedFetch(`${uri_souls}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      throw new Error(`Error loading agent profile (${res.status})`);
    }
    const json = await res.json();
    const profiles = (json.data ?? json) as AgentProfile[];
    // The souls list can omit operations; the carousel treats these as profiles.
    return profiles.map((p) => ({ ...p, operations: p.operations ?? [] }));
  },

  getAgentOperations: async (soulId: string): Promise<AgentOperation[]> => {
    const res = await _protectedFetch(`${uri_souls}/${soulId}/operations`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      throw new Error(`Error loading operations (${res.status})`);
    }
    const json = await res.json();
    return json.data ?? [];
  },

  createAgentOperation: async (
    soulId: string,
    operation: string,
  ): Promise<AgentOperation> => {
    const res = await _protectedFetch(`${uri_souls}/${soulId}/operations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ operation }),
    });
    if (!res.ok) throw new Error(`Error assigning operation (${res.status})`);
    const json = await res.json();
    return json.data ?? json;
  },

  deleteAgentOperation: async (
    soulId: string,
    operationId: string,
  ): Promise<void> => {
    const res = await _protectedFetch(
      `${uri_souls}/${soulId}/operations/${operationId}`,
      { method: 'DELETE', headers: { 'Content-Type': 'application/json' } },
    );
    if (!res.ok) throw new Error(`Error removing operation (${res.status})`);
  },

  getShiftReports: async (
    params: { soulId?: string; venueId?: string } = {},
  ): Promise<ShiftReportSummary[]> => {
    const search = new URLSearchParams();
    if (params.soulId) search.set('soulId', params.soulId);
    if (params.venueId) search.set('venueId', params.venueId);
    const url = search.toString()
      ? `${uri_shift_reports}?${search.toString()}`
      : uri_shift_reports;
    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      throw new Error(`Error loading shift reports (${res.status})`);
    }
    const json = await res.json();
    return json.data ?? [];
  },

  getShiftReport: async (id: string): Promise<ShiftReportDetail> => {
    const res = await _protectedFetch(`${uri_shift_reports}/${id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      throw new Error(`Error loading shift report (${res.status})`);
    }
    const json = await res.json();
    return json.data ?? json;
  },

  getEventReports: async (
    params: { soulId?: string; eventId?: string } = {},
  ): Promise<EventReportSummary[]> => {
    const search = new URLSearchParams();
    if (params.soulId) search.set('soulId', params.soulId);
    if (params.eventId) search.set('eventId', params.eventId);
    const url = search.toString()
      ? `${uri_event_reports}?${search.toString()}`
      : uri_event_reports;
    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      throw new Error(`Error loading event reports (${res.status})`);
    }
    const json = await res.json();
    return json.data ?? [];
  },

  getEventReport: async (id: string): Promise<EventReportDetail> => {
    const res = await _protectedFetch(`${uri_event_reports}/${id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      throw new Error(`Error loading event report (${res.status})`);
    }
    const json = await res.json();
    return json.data ?? json;
  },

  getWeddingReports: async (
    params: { soulId?: string } = {},
  ): Promise<WeddingReportSummary[]> => {
    const search = new URLSearchParams();
    if (params.soulId) search.set('soulId', params.soulId);
    const url = search.toString()
      ? `${uri_wedding_reports}?${search.toString()}`
      : uri_wedding_reports;
    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      throw new Error(`Error loading wedding reports (${res.status})`);
    }
    const json = await res.json();
    return json.data ?? [];
  },

  getWeddingReport: async (id: string): Promise<WeddingReportDetail> => {
    const res = await _protectedFetch(`${uri_wedding_reports}/${id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      throw new Error(`Error loading wedding report (${res.status})`);
    }
    const json = await res.json();
    return json.data ?? json;
  },

  getTourReports: async (
    params: { soulId?: string } = {},
  ): Promise<TourReportSummary[]> => {
    const search = new URLSearchParams();
    if (params.soulId) search.set('soulId', params.soulId);
    const url = search.toString()
      ? `${uri_tour_reports}?${search.toString()}`
      : uri_tour_reports;
    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      throw new Error(`Error loading tour reports (${res.status})`);
    }
    const json = await res.json();
    return json.data ?? [];
  },

  getTourReport: async (id: string): Promise<TourReportDetail> => {
    const res = await _protectedFetch(`${uri_tour_reports}/${id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      throw new Error(`Error loading tour report (${res.status})`);
    }
    const json = await res.json();
    return json.data ?? json;
  },

  getHotelReports: async (
    params: { soulId?: string; venueId?: string } = {},
  ): Promise<HotelReportSummary[]> => {
    const search = new URLSearchParams();
    if (params.soulId) search.set('soulId', params.soulId);
    if (params.venueId) search.set('venueId', params.venueId);
    const url = search.toString()
      ? `${uri_hotel_reports}?${search.toString()}`
      : uri_hotel_reports;
    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      throw new Error(`Error loading hotel reports (${res.status})`);
    }
    const json = await res.json();
    return json.data ?? [];
  },

  getHotelReport: async (id: string): Promise<HotelReportDetail> => {
    const res = await _protectedFetch(`${uri_hotel_reports}/${id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      throw new Error(`Error loading hotel report (${res.status})`);
    }
    const json = await res.json();
    return json.data ?? json;
  },

  getAgentContacts: async (soulId: string): Promise<AgentContact[]> => {
    const res = await _protectedFetch(`${uri_souls}/${soulId}/contacts`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      throw new Error(`Error loading contacts (${res.status})`);
    }
    const json = await res.json();
    return json.data ?? [];
  },

  createAgentContact: async (
    soulId: string,
    userId: string,
  ): Promise<AgentContact> => {
    const res = await _protectedFetch(`${uri_souls}/${soulId}/contacts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId }),
    });
    if (!res.ok) throw new Error(`Error adding contact (${res.status})`);
    const json = await res.json();
    return json.data ?? json;
  },

  deleteAgentContact: async (
    soulId: string,
    contactId: string,
  ): Promise<void> => {
    const res = await _protectedFetch(
      `${uri_souls}/${soulId}/contacts/${contactId}`,
      { method: 'DELETE', headers: { 'Content-Type': 'application/json' } },
    );
    if (!res.ok) throw new Error(`Error removing contact (${res.status})`);
  },

  getEventStaff: async (id: string): Promise<EventStaffMember[]> => {
    const res = await _protectedFetch(`${uri_event_reports}/${id}/staff`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      throw new Error(`Error loading event staff (${res.status})`);
    }
    const json = await res.json();
    return json.data ?? [];
  },

  // ===== AI Chats =====
  getAiChats: async (
    params: { limit?: number; cursor?: string } = {},
  ): Promise<AiChat[]> => {
    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.cursor) searchParams.set('cursor', params.cursor);
    const qs = searchParams.toString();
    const url = qs ? `${uri_ai_chats}?${qs}` : uri_ai_chats;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const data = await res.json();
    return data.chats;
  },
  getAiChat: async (id: string): Promise<AiChatDetail> => {
    const res = await _protectedFetch(`${uri_ai_chats}/${id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    return res.json();
  },
  deleteAiChat: async (id: string): Promise<void> => {
    const res = await _protectedFetch(`${uri_ai_chats}/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      throw new Error(`Error deleting chat (${res.status})`);
    }
  },
  updateAiChatTitle: async (id: string, title: string): Promise<AiChat> => {
    const res = await _protectedFetch(`${uri_ai_chats}/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    });
    return res.json();
  },
  getBusinessObjects: async (
    chatId: string,
  ): Promise<Record<string, import('@Types/soul').BusinessObject>> => {
    const res = await _protectedFetch(
      `${uri_ai_chats}/${chatId}/business-objects`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      },
    );
    const json = await res.json();
    return json.data;
  },
  mergeBusinessObjectData: async (
    chatId: string,
    key: string,
    data: Record<string, unknown>,
  ) => {
    const res = await _protectedFetch(
      `${uri_ai_chats}/${chatId}/business-objects/${key}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data }),
      },
    );
    if (!res.ok) {
      throw new Error(`Failed to update draft (${res.status})`);
    }
    return (await res.json()).data;
  },
  getBusinessObjectRecords: async (
    chatId: string,
    key: string,
    params: { limit?: number; offset?: number } = {},
  ): Promise<{
    records: Record<string, any>[];
    total: number;
    offset: number;
    limit: number;
  }> => {
    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));
    const qs = searchParams.toString();
    const url = `${uri_ai_chats}/${chatId}/business-objects/${encodeURIComponent(key)}${qs ? `?${qs}` : ''}`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await res.json();
    return json.data;
  },
  getMergeTags: async () => {
    const res = await _protectedFetch(_getFullPath(DOMAIN, '/ai/merge-tags'), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    return res.json();
  },
  executeAction: async (chatId: string, payload: Record<string, any>) => {
    const res = await _protectedFetch(`${uri_ai_chats}/${chatId}/actions`, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json' },
    });
    return res.json();
  },

  // ===== Eval Suites =====
  getEvalSuites: async (
    params: { status?: 'DRAFT' | 'PUBLISHED' } = {},
  ): Promise<EvalSuiteSummary[]> => {
    const search = new URLSearchParams();
    if (params.status) search.set('status', params.status);
    const url = search.toString()
      ? `${uri_eval_suites}?${search.toString()}`
      : uri_eval_suites;
    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await res.json();
    return json.data ?? [];
  },
  getEvalSuite: async (id: string): Promise<EvalSuiteDetail> => {
    const res = await _protectedFetch(`${uri_eval_suites}/${id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await res.json();
    return json.data;
  },
  createEvalSuite: async (
    payload: CreateSuitePayload,
  ): Promise<EvalSuiteSummary> => {
    const res = await _protectedFetch(uri_eval_suites, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`Error creating eval suite (${res.status})`);
    const json = await res.json();
    return json.data;
  },
  updateEvalSuite: async (
    id: string,
    payload: UpdateSuitePayload,
  ): Promise<EvalSuiteSummary> => {
    const res = await _protectedFetch(`${uri_eval_suites}/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`Error updating eval suite (${res.status})`);
    const json = await res.json();
    return json.data;
  },
  publishEvalSuite: async (id: string): Promise<EvalSuiteSummary> => {
    const res = await _protectedFetch(`${uri_eval_suites}/${id}/_publish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error(`Error publishing eval suite (${res.status})`);
    const json = await res.json();
    return json.data;
  },
  cloneEvalSuite: async (id: string): Promise<EvalSuiteDetail> => {
    const res = await _protectedFetch(`${uri_eval_suites}/${id}/_clone`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error(`Error cloning eval suite (${res.status})`);
    const json = await res.json();
    return json.data;
  },
  deleteEvalSuite: async (id: string): Promise<void> => {
    const res = await _protectedFetch(`${uri_eval_suites}/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error(`Error deleting eval suite (${res.status})`);
  },
  // ===== Eval Cases =====
  createEvalCase: async (
    suiteId: string,
    payload: CreateCasePayload,
  ): Promise<EvalCase> => {
    const res = await _protectedFetch(`${uri_eval_suites}/${suiteId}/cases`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`Error creating eval case (${res.status})`);
    const json = await res.json();
    return json.data;
  },
  getEvalCase: async (id: string): Promise<EvalCase> => {
    const res = await _protectedFetch(`${uri_eval_cases}/${id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error(`Error fetching eval case (${res.status})`);
    const json = await res.json();
    return json.data;
  },
  updateEvalCase: async (
    id: string,
    payload: UpdateCasePayload,
  ): Promise<EvalCase> => {
    const res = await _protectedFetch(`${uri_eval_cases}/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`Error updating eval case (${res.status})`);
    const json = await res.json();
    return json.data;
  },
  deleteEvalCase: async (id: string): Promise<void> => {
    const res = await _protectedFetch(`${uri_eval_cases}/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error(`Error deleting eval case (${res.status})`);
  },
  promoteCaseToExample: async (
    caseId: string,
    soulId: string,
  ): Promise<SoulExample> => {
    const res = await _protectedFetch(
      `${uri_eval_cases}/${caseId}/_promote-to-example`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ soulId }),
      },
    );
    if (!res.ok) throw new Error(`Error promoting case (${res.status})`);
    const json = await res.json();
    return json.data;
  },
  // ===== Eval Runs =====
  runEvalSuite: async (
    suiteId: string,
    payload: RunSuitePayload,
  ): Promise<{ runId: string }> => {
    const res = await _protectedFetch(`${uri_eval_suites}/${suiteId}/_run`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`Error starting eval run (${res.status})`);
    const json = await res.json();
    return json.data;
  },
  cancelEvalRun: async (id: string): Promise<EvalRunSummary> => {
    const res = await _protectedFetch(`${uri_eval_runs}/${id}/_cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error(`Error cancelling eval run (${res.status})`);
    const json = await res.json();
    return json.data;
  },
  reEvaluateRun: async (id: string): Promise<EvalRunDetail> => {
    const res = await _protectedFetch(`${uri_eval_runs}/${id}/_re-evaluate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error(`Error re-evaluating run (${res.status})`);
    const json = await res.json();
    return json.data;
  },
  getEvalRuns: async (
    params: { suiteId?: string; soulId?: string; status?: string } = {},
  ): Promise<EvalRunSummary[]> => {
    const search = new URLSearchParams();
    if (params.suiteId) search.set('suiteId', params.suiteId);
    if (params.soulId) search.set('soulId', params.soulId);
    if (params.status) search.set('status', params.status);
    const url = search.toString()
      ? `${uri_eval_runs}?${search.toString()}`
      : uri_eval_runs;
    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await res.json();
    return json.data ?? [];
  },
  getEvalRun: async (id: string): Promise<EvalRunDetail> => {
    const res = await _protectedFetch(`${uri_eval_runs}/${id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await res.json();
    return json.data;
  },
  // Returns a URL to be opened via fetch+ReadableStream for NDJSON event streaming.
  getEvalRunStreamUrl: (id: string, since?: number): string => {
    const url = new URL(`${uri_eval_runs}/${id}/_stream`);
    if (since !== undefined) url.searchParams.set('since', String(since));
    return url.toString();
  },
  // ===== Import =====
  addChatToEvalSuite: async (
    chatId: string,
    payload: {
      suiteId: string;
      caseName?: string;
      lastAssistantAsExpected: boolean;
    },
  ): Promise<EvalCase> => {
    const res = await _protectedFetch(
      `${uri_ai_chats}/${chatId}/_to-eval-case`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    );
    if (!res.ok)
      throw new Error(`Error adding chat to eval suite (${res.status})`);
    const json = await res.json();
    return json.data;
  },
  // ===== Soul Examples =====
  getSoulExamples: async (soulId: string): Promise<SoulExample[]> => {
    const res = await _protectedFetch(`${uri_souls}/${soulId}/examples`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await res.json();
    return json.data ?? [];
  },
  getSoulExample: async (id: string): Promise<SoulExample> => {
    const res = await _protectedFetch(`${uri_soul_examples}/${id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await res.json();
    return json.data;
  },
  createSoulExample: async (
    soulId: string,
    payload: CreateSoulExamplePayload,
  ): Promise<SoulExample> => {
    const res = await _protectedFetch(`${uri_souls}/${soulId}/examples`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`Error creating example (${res.status})`);
    const json = await res.json();
    return json.data;
  },
  updateSoulExample: async (
    id: string,
    payload: UpdateSoulExamplePayload,
  ): Promise<SoulExample> => {
    const res = await _protectedFetch(`${uri_soul_examples}/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`Error updating example (${res.status})`);
    const json = await res.json();
    return json.data;
  },
  deleteSoulExample: async (id: string): Promise<void> => {
    const res = await _protectedFetch(`${uri_soul_examples}/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error(`Error deleting example (${res.status})`);
  },

  // ===== Job Runs =====
  getJobRuns: async (params: Record<string, any> = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        queryParams.append(key, String(value));
      }
    });
    const queryString = queryParams.toString();
    const url = queryString ? `${uri_job_runs}?${queryString}` : uri_job_runs;
    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    return res.json();
  },
  getJobRun: async (id: string) => {
    const res = await _protectedFetch(`${uri_job_runs}/${id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    return res.json();
  },
  getJobRunTypes: async () => {
    const res = await _protectedFetch(`${uri_job_runs}/types`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    return res.json();
  },
  getJobRunsByType: async (
    typeName: string,
    params: { from?: string; to?: string } = {},
  ) => {
    const queryParams = new URLSearchParams();
    if (params.from) queryParams.append('from', params.from);
    if (params.to) queryParams.append('to', params.to);
    const qs = queryParams.toString();
    const url = `${uri_job_runs}/type/${encodeURIComponent(typeName)}${qs ? `?${qs}` : ''}`;
    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    return res.json();
  },
  runSummarizeActivity: async (
    body: {
      startDate?: string;
      endDate?: string;
      dry?: boolean;
      venueId?: string;
      force?: boolean;
    } = {},
  ) => {
    const res = await _protectedFetch(
      `${uri_job_runs}/summarize-activity/_run`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    return res.json();
  },

  runSummarizeEvents: async (
    body: { lookbackDays?: number; dry?: boolean; eventId?: string } = {},
  ) => {
    const res = await _protectedFetch(`${uri_job_runs}/summarize-events/_run`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  },

  runWeddingRollup: async (
    body: {
      startDate?: string;
      endDate?: string;
      dry?: boolean;
      sendEmail?: boolean;
    } = {},
  ) => {
    const res = await _protectedFetch(`${uri_job_runs}/wedding-rollup/_run`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  },

  runTourRollup: async (
    body: {
      startDate?: string;
      endDate?: string;
      dry?: boolean;
      sendEmail?: boolean;
    } = {},
  ) => {
    const res = await _protectedFetch(`${uri_job_runs}/tour-rollup/_run`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  },

  runHotelRollup: async (
    body: {
      startDate?: string;
      endDate?: string;
      dry?: boolean;
      sendEmail?: boolean;
      venueId?: string;
    } = {},
  ) => {
    const res = await _protectedFetch(`${uri_job_runs}/hotel-rollup/_run`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  },

  runLapseDetection: async () => {
    const res = await _protectedFetch(`${uri_job_runs}/detect-lapses/_run`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    return res.json();
  },

  getReferrals: async (
    searchParams = {},
  ): Promise<PaginatedResponse<Referral>> => {
    const queryParams = new URLSearchParams(searchParams).toString();
    const url = queryParams
      ? `${uri_referrals}/?${queryParams}`
      : `${uri_referrals}`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return await res.json();
  },
  getReferralById: async (referralId): Promise<Referral> => {
    const url = `${uri_referrals}/?id=${referralId}`;

    const res = await _protectedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return await res.json();
  },
  getCacheEntries: async (
    params: {
      tenant?: string;
      restaurantGuid?: string;
      resource?: string;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.tenant) qs.append('tenant', params.tenant);
    if (params.restaurantGuid)
      qs.append('restaurantGuid', params.restaurantGuid);
    if (params.resource) qs.append('resource', params.resource);
    const query = qs.toString();
    const url = query ? `${uri_admin_cache}?${query}` : uri_admin_cache;
    const res = await _protectedFetch(url, { method: 'GET' });
    return res.json();
  },
  getCacheEntry: async (key: string) => {
    const url = `${uri_admin_cache}/entry?key=${encodeURIComponent(key)}`;
    const res = await _protectedFetch(url, { method: 'GET' });
    return res.json();
  },
  getCacheStats: async () => {
    const res = await _protectedFetch(`${uri_admin_cache}/stats`, {
      method: 'GET',
    });
    return res.json();
  },
  deleteCacheEntries: async (params: {
    key?: string;
    tenant?: string;
    restaurantGuid?: string;
    resource?: string;
    all?: boolean;
  }) => {
    const qs = new URLSearchParams();
    if (params.key) qs.append('key', params.key);
    if (params.tenant) qs.append('tenant', params.tenant);
    if (params.restaurantGuid)
      qs.append('restaurantGuid', params.restaurantGuid);
    if (params.resource) qs.append('resource', params.resource);
    if (params.all) qs.append('all', 'true');
    const query = qs.toString();
    const url = query ? `${uri_admin_cache}?${query}` : uri_admin_cache;
    const res = await _protectedFetch(url, { method: 'DELETE' });
    return res.json();
  },
  createDashboard: async (dashboardData: Record<string, unknown>) => {
    const res = await _protectedFetch(uri_dashboard, {
      method: 'POST',
      body: JSON.stringify(dashboardData),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return res;
  },
  updateDashboard: async (
    dashboardId: string,
    payload: Record<string, unknown>,
  ) => {
    const res = await _protectedFetch(`${uri_dashboard}/${dashboardId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      console.error('Error updating Dashboard: HTTP', res.status);
      throw new Error(`Error updating Dashboard (${res.status})`);
    }

    return await res.json();
  },
  getDashboards: async (searchParams = {}) => {
    try {
      const queryParams = new URLSearchParams(searchParams).toString();
      const url = queryParams
        ? `${uri_dashboard}?${queryParams}`
        : `${uri_dashboard}`;

      const res = await _protectedFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return await res.json();
    } catch (error) {
      console.error('Error getting Dashborads:', error);
    }
  },
  getDashboard: async (dashboardId: string, searchParams = {}) => {
    try {
      const queryParams = new URLSearchParams(searchParams).toString();
      const url = queryParams
        ? `${uri_dashboard}/${dashboardId}?${queryParams}`
        : `${uri_dashboard}/${dashboardId}`;

      const res = await _protectedFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return await res.json();
    } catch (error) {
      console.error('Error getting Dashboards:', error);
    }
  },
  deleteDashboard: async (dashboardId: string, deletedBy: string) => {
    const res = await _protectedFetch(`${uri_dashboard}/${dashboardId}`, {
      method: 'DELETE',
      body: JSON.stringify({ deletedBy }),
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`Failed to delete Dashboard (${res.status})`);
    }

    return res;
  },

  createWorkspace: async (workspaceData: Record<string, unknown>) => {
    const res = await _protectedFetch(uri_workspace, {
      method: 'POST',
      body: JSON.stringify(workspaceData),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return res;
  },
  updateWorkspace: async (
    workspaceId: string,
    payload: Record<string, unknown>,
  ) => {
    const res = await _protectedFetch(`${uri_workspace}/${workspaceId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      console.error('Error updating Workspace: HTTP', res.status);
      throw new Error(`Error updating Workspace (${res.status})`);
    }

    return await res.json();
  },
  getWorkspaces: async (searchParams = {}) => {
    try {
      const queryParams = new URLSearchParams(searchParams).toString();
      const url = queryParams
        ? `${uri_workspace}?${queryParams}`
        : `${uri_workspace}`;

      const res = await _protectedFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return await res.json();
    } catch (error) {
      console.error('Error getting Workspaces:', error);
    }
  },
  getWorkspace: async (workspaceId: string, searchParams = {}) => {
    try {
      const queryParams = new URLSearchParams(searchParams).toString();
      const url = queryParams
        ? `${uri_workspace}/${workspaceId}?${queryParams}`
        : `${uri_workspace}/${workspaceId}`;

      const res = await _protectedFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return await res.json();
    } catch (error) {
      console.error('Error getting Workspaces:', error);
    }
  },
  deleteWorkspace: async (workspaceId: string, deletedBy: string) => {
    const res = await _protectedFetch(`${uri_workspace}/${workspaceId}`, {
      method: 'DELETE',
      body: JSON.stringify({ deletedBy }),
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`Failed to delete Workspace (${res.status})`);
    }

    return res;
  },

  // ─── Omni-search ───
  omniSearch: async (body: Record<string, any>) => {
    const res = await _protectedFetch(`${uri_search}/_omni`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`Omni-search failed (${res.status})`);
    return res.json();
  },
  // Full-text fish search over the Meilisearch `fish` index (all fish, guests included).
  searchFish: async (q: string, limit = 25) => {
    const url = `${uri_search}/fish?q=${encodeURIComponent(q)}&limit=${limit}`;
    const res = await _protectedFetch(url, { method: 'GET' });
    if (!res.ok) throw new Error(`Fish search failed (${res.status})`);
    return res.json();
  },
  getSearchSchema: async (index: string) => {
    const res = await _protectedFetch(`${uri_search}/${index}/_schema`, {
      method: 'GET',
    });
    if (!res.ok)
      throw new Error(`Failed to load schema for '${index}' (${res.status})`);
    return res.json();
  },
  getSearchFieldValues: async (index: string, field: string, q?: string) => {
    const base = `${uri_search}/${index}/_schema/${encodeURIComponent(field)}/_values`;
    const url = q ? `${base}?q=${encodeURIComponent(q)}` : base;
    const res = await _protectedFetch(url, { method: 'GET' });
    if (!res.ok)
      throw new Error(`Failed to load values for '${field}' (${res.status})`);
    return res.json();
  },

  /* ----------------------- Content editor (content slots) ----------------------- */

  getApps: async (): Promise<TenantApp[]> => {
    const res = await _protectedFetch(uri_apps, { method: 'GET' });
    if (!res.ok) throw new Error(`Failed to load apps (${res.status})`);
    return res.json();
  },

  getContentSlots: async (appId: string): Promise<ContentSlotSummary[]> => {
    // App scoping rides the app-id header (like tenant-id), not the URL.
    const res = await _protectedFetch(uri_content_slots, {
      method: 'GET',
      headers: { 'app-id': appId },
    });
    if (!res.ok)
      throw new Error(`Failed to load content slots (${res.status})`);
    return res.json();
  },

  getContentSlot: async (id: string): Promise<ContentSlot> => {
    const res = await _protectedFetch(`${uri_content_slots}/${id}`, {
      method: 'GET',
    });
    if (!res.ok) throw new Error(`Failed to load content slot (${res.status})`);
    return res.json();
  },

  createContentSlot: async (
    payload: CreateContentSlotPayload,
  ): Promise<ContentSlot> => {
    // appId travels as the app-id header; the body carries only slot fields.
    const { appId, ...body } = payload;
    const res = await _protectedFetch(uri_content_slots, {
      method: 'POST',
      headers: { 'app-id': appId },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Error creating content slot (${res.status})`,
      );
    }
    return res.json();
  },

  updateContentSlot: async (
    id: string,
    payload: UpdateContentSlotPayload,
  ): Promise<ContentSlot> => {
    const res = await _protectedFetch(`${uri_content_slots}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Error updating content slot (${res.status})`,
      );
    }
    return res.json();
  },

  saveContentSlotDraft: async (
    id: string,
    payload: SaveContentSlotDraftPayload,
  ): Promise<ContentSlot> => {
    const res = await _protectedFetch(`${uri_content_slots}/${id}/draft`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      // 409 = another editor saved since this client loaded (stale save).
      const error = new Error(
        errorData.message || `Error saving draft (${res.status})`,
      ) as Error & { status?: number };
      error.status = res.status;
      throw error;
    }
    return res.json();
  },

  publishContentSlot: async (id: string): Promise<ContentSlot> => {
    const res = await _protectedFetch(`${uri_content_slots}/${id}/publish`, {
      method: 'POST',
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Error publishing content slot (${res.status})`,
      );
    }
    return res.json();
  },

  unpublishContentSlot: async (id: string): Promise<ContentSlot> => {
    const res = await _protectedFetch(`${uri_content_slots}/${id}/unpublish`, {
      method: 'POST',
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Error unpublishing content slot (${res.status})`,
      );
    }
    return res.json();
  },

  discardContentSlotDraft: async (id: string): Promise<ContentSlot> => {
    const res = await _protectedFetch(`${uri_content_slots}/${id}/discard`, {
      method: 'POST',
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Error discarding draft (${res.status})`,
      );
    }
    return res.json();
  },

  deleteContentSlot: async (id: string): Promise<void> => {
    const res = await _protectedFetch(`${uri_content_slots}/${id}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Error deleting content slot (${res.status})`,
      );
    }
  },
};

const _getFullPath = (domain: string, uri: string) => {
  return `${domain}${uri}`;
};

export const _protectedFetch = async (
  uri: string,
  args: any = {},
  toastRestaurantGuid?: string,
) => {
  // Read the admin-scoped cookie; fall back to the legacy shared name during
  // the rollout window so existing in-flight admin sessions keep working.
  const accessToken = cookies.get('at-admin') || cookies.get('at');

  const headers: Record<string, string> = {
    ...args.headers,
    'Authorization': `bearer ${accessToken}`,
    'tenant-id': requireTenantId(),
  };

  const isFormData = args.body instanceof FormData;
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  if (toastRestaurantGuid) {
    headers['Toast-Restaurant-GUID'] = toastRestaurantGuid;
  }

  const res = await fetch(uri, {
    ...args,
    headers,
  });

  if (res.status === 401) {
    serviceMethods.clearAuthTokens();
    if (window.location.pathname !== '/login') {
      window.location.reload();
    }
  }

  if (!res.ok) {
    const errRes = res.clone();
    const errorData = await errRes.json();
    console.error(errorData.message || `HTTP error! status: ${res.status}`);
  }

  return res;
};

export const { readAllFish } = serviceMethods;
export { uri_ai_chat };

export default { ...serviceMethods };

const uri_get_all_flows = _getFullPath(DOMAIN, '/flow/get-all-flows');
const uri_flow = _getFullPath(DOMAIN, '/flow');
const uri_flow_category = _getFullPath(DOMAIN, '/flow-category');
const uri_get_full_flow = _getFullPath(
  DOMAIN,
  '/flow/get-flow-with-stages-and-forms',
);
const uri_flow_stage = _getFullPath(DOMAIN, '/flow-stage');
const uri_get_all_flow_stages = _getFullPath(
  DOMAIN,
  '/flow-stage/get-all-flow-stages',
);
const uri_get_all_forms = _getFullPath(DOMAIN, '/typeform-form/all-forms');
const uri_get_all_custom_forms = _getFullPath(
  DOMAIN,
  '/custom-form/get-all-custom-forms',
);
const uri_custom_form = _getFullPath(DOMAIN, '/custom-form');
const uri_custom_form_response = _getFullPath(DOMAIN, '/custom-form-response');
const uri_get_flow_stage_types = _getFullPath(DOMAIN, '/flow-stage-types');
const uri_outbound_message = _getFullPath(DOMAIN, '/outbound-message');
const uri_tag = _getFullPath(DOMAIN, '/tag');
const uri_get_all_tags_with_query = _getFullPath(
  DOMAIN,
  '/tag/get-all-tags-with-query',
);
const uri_segment = _getFullPath(DOMAIN, '/segment');
const uri_tag_bulk = _getFullPath(DOMAIN, '/tag/bulk');
const uri_fish = _getFullPath(DOMAIN, '/fish');
const uri_fish_v2 = _getFullPath(DOMAIN_V2, '/fish');
const uri_fish_bulk = _getFullPath(DOMAIN, '/fish/bulk');
const uri_immediate_send_sms = _getFullPath(DOMAIN, '/sms/send-immediately');
const uri_sms_threads = _getFullPath(DOMAIN, '/sms/threads');
const uri_send_otp = _getFullPath(DOMAIN, '/auth/send-otp');
const uri_verify_user_otp = _getFullPath(DOMAIN, '/auth/verify-user-otp');
const uri_omni_dashboard = _getFullPath(DOMAIN, '/dashboard');
const uri_dashboard = _getFullPath(DOMAIN, '/dashboards');
const uri_workspace = _getFullPath(DOMAIN, '/workspace');
const uri_run_script = _getFullPath(DOMAIN, '/run-script');
const uri_merge_tags = _getFullPath(DOMAIN, '/merge-tags');
const uri_message_template = _getFullPath(DOMAIN, '/message-template');
const uri_triggers = _getFullPath(DOMAIN, '/triggers');
const uri_get_flow_triggers = _getFullPath(DOMAIN, '/triggers/by-flow-id');
const uri_get_flow_stage_triggers = _getFullPath(
  DOMAIN,
  '/triggers/by-flow-stage-id',
);
const uri_toggle_trigger_active = _getFullPath(
  DOMAIN,
  '/triggers/toggle-active',
);
const uri_user_roles = _getFullPath(DOMAIN, '/user-roles');
const uri_permissions = _getFullPath(DOMAIN, '/permissions');
const uri_fish_roles = _getFullPath(DOMAIN, '/fish-roles');
const uri_users = _getFullPath(DOMAIN, '/users');
const uri_user_access = _getFullPath(DOMAIN, '/user-roles/access');
const uri_inbox = _getFullPath(DOMAIN, '/inbox');
const uri_phone_numbers = _getFullPath(DOMAIN, '/phone-numbers');
const uri_attached_inbox_types = _getFullPath(DOMAIN, '/attached-inbox-types');
const uri_user_fields = _getFullPath(DOMAIN, '/user-fields');
const uri_get_next_quarterly_spend = _getFullPath(
  DOMAIN,
  '/subscription/ui-get-next-quarterly-spend',
);
const uri_get_subscription_tiers = _getFullPath(DOMAIN, '/subscription-tier');
const uri_get_subscription_tiers_with_meta = _getFullPath(
  DOMAIN,
  '/subscription-tier/with-meta',
);
const uri_get_subscription_tier_by_id = _getFullPath(
  DOMAIN,
  '/subscription-tier',
);
const uri_subscription = _getFullPath(DOMAIN, '/subscription');
const uri_get_subscription = _getFullPath(DOMAIN, '/subscription/by-id');
const uri_get_purchase_by_id = _getFullPath(DOMAIN, '/purchases/by-id');
const uri_purchases = _getFullPath(DOMAIN, '/purchases');
const uri_activity = _getFullPath(DOMAIN, '/fish/:fishId/activity');
const uri_get_restaurants = _getFullPath(DOMAIN, '/toast/restaurants');
const uri_wallet = _getFullPath(DOMAIN, '/wallet/');
const uri_field_options = _getFullPath(DOMAIN, '/field-options/');
const uri_get_registration_code_by_id = _getFullPath(
  DOMAIN,
  '/subscription/get-registration-code-discount',
);
const uri_get_all_registration_codes = _getFullPath(
  DOMAIN,
  '/subscription/get-all-registration-codes',
);
const uri_registration_code = _getFullPath(
  DOMAIN,
  '/subscription/registration-code',
);
const uri_flow_stage_results = _getFullPath(DOMAIN, '/flow-stage-result');
const uri_typeform_response = _getFullPath(DOMAIN, '/typeform-form/response');
const uri_toast_payment_intents = _getFullPath(
  DOMAIN,
  '/admin/toast/payment-intents',
);
const uri_toast_menus = _getFullPath(DOMAIN, '/admin/toast/menus');
const uri_toast_orders = _getFullPath(DOMAIN, '/admin/toast/orders');
const uri_toast_guids = _getFullPath(DOMAIN, '/admin/toast/guid');
const uri_toast_guid_groups = _getFullPath(DOMAIN, '/admin/toast/guid-group');
const uri_toast_guid_settings = _getFullPath(
  DOMAIN,
  '/admin/toast/guid-settings',
);
const uri_toast_service_charges = _getFullPath(
  DOMAIN,
  '/admin/toast/service-charges',
);
const uri_toast_dining_options = _getFullPath(
  DOMAIN,
  '/admin/toast/dining-options',
);

const uri_promo_codes = _getFullPath(DOMAIN, '/promo-codes');
const uri_redemptions = _getFullPath(DOMAIN, '/redemptions');
const uri_partner_referral_codes = _getFullPath(
  DOMAIN,
  '/partner-referral-code',
);

const uri_venues = _getFullPath(DOMAIN, '/venues');
const uri_spaces = _getFullPath(DOMAIN, '/spaces');

const uri_assets = _getFullPath(DOMAIN, '/assets');
const uri_asset_tags = _getFullPath(DOMAIN, '/asset-tags');
const uri_images = _getFullPath(DOMAIN, '/images');

const uri_ticketed_events = _getFullPath(DOMAIN, '/ticketed-events');
const uri_ticketed_event_tickets = _getFullPath(
  DOMAIN,
  '/ticketed-event-tickets',
);
const uri_ticketed_event_tags = _getFullPath(DOMAIN, '/ticketed-event-tags');
const uri_ticketed_event_categories = _getFullPath(
  DOMAIN,
  '/ticketed-event-categories',
);

const uri_souls = _getFullPath(DOMAIN, '/ai/souls');
const uri_shift_reports = _getFullPath(DOMAIN, '/shift-reports');
const uri_event_reports = _getFullPath(DOMAIN, '/event-reports');
const uri_wedding_reports = _getFullPath(DOMAIN, '/wedding-reports');
const uri_tour_reports = _getFullPath(DOMAIN, '/tour-reports');
const uri_hotel_reports = _getFullPath(DOMAIN, '/hotel-reports');
const uri_ai_chat = _getFullPath(DOMAIN, '/ai/chat');
const uri_ai_chats = _getFullPath(DOMAIN, '/ai/chats');
const uri_eval_suites = _getFullPath(DOMAIN, '/ai/eval-suites');
const uri_eval_cases = _getFullPath(DOMAIN, '/ai/eval-cases');
const uri_eval_runs = _getFullPath(DOMAIN, '/ai/eval-runs');
const uri_soul_examples = _getFullPath(DOMAIN, '/ai/soul-examples');
const uri_events = _getFullPath(DOMAIN, '/events');
const uri_event_booking_events = _getFullPath(DOMAIN, '/event-booking/events');
const uri_event_booking_event_types = _getFullPath(
  DOMAIN,
  '/event-booking/event-types',
);
const uri_event_booking_statuses = _getFullPath(
  DOMAIN,
  '/event-booking/statuses',
);
const uri_ticketed_event_organizers = _getFullPath(
  DOMAIN,
  '/ticketed-event-organizers',
);
const uri_search = _getFullPath(DOMAIN, '/search');
const uri_referrals = _getFullPath(DOMAIN, '/referral');
const uri_job_runs = _getFullPath(DOMAIN, '/job-runs');
const uri_admin_cache = _getFullPath(DOMAIN, '/admin/cache');
const uri_content_slots = _getFullPath(DOMAIN, '/content-slots');
const uri_apps = _getFullPath(DOMAIN, '/apps');
const uri_emailThread = _getFullPath(DOMAIN, '/email-thread');
